import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import PortalStatusSelector from "@/components/admin/PortalStatusSelector";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import {
  Sparkles,
  TrendingUp,
  Users,
  Monitor,
  Plug,
  Plus,
  RefreshCw,
  Eye,
  Settings,
  Trash2,
  Copy,
  Link2,
  Send,
  CheckCircle2,
  Clock,
  Search,
  ChevronRight,
  ChevronDown,
  Building2,
  User,
  Mail,
  ExternalLink,
  Edit2,
  AlertTriangle,
  ClipboardList,
  FileText,
  Download,
  Image as ImageIcon,
  Upload,
  X,
  Map,
  Shield,
  Save,
  Calendar,
  LayoutDashboard,
  RotateCcw,
  Video,
  GraduationCap,
  ChefHat,
  Plane,
  ShoppingBag
} from "lucide-react";
import PortalConfiguratorModal from "@/components/PortalConfiguratorModal";

// ============================================================================
// IMAGE UPLOAD COMPONENT - Uploads images instead of URLs
// ============================================================================
function ImageUploadField({ 
  label, 
  value, 
  onChange, 
  subCategoryId,
  imageType = "cover",
  placeholder = "Upload an image",
  previewSize = "h-32",
  accept = "image/*"
}) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(value || "");
  }, [value]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Max 10MB allowed.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", imageType);

      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-upload/${subCategoryId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        const imageUrl = data.data_url || data.url;
        setPreview(imageUrl);
        onChange(imageUrl);
        toast.success("Image uploaded!");
      } else {
        toast.error("Failed to upload image");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Error uploading image");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview("");
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-col gap-2">
        {/* Preview */}
        {preview && (
          <div className="relative group">
            <div className={`${previewSize} w-full rounded-lg border overflow-hidden bg-gray-100`}>
              <img 
                src={preview} 
                alt={label} 
                className="w-full h-full object-cover"
              />
            </div>
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* Upload Button */}
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleUpload}
            className="hidden"
            id={`upload-${imageType}-${subCategoryId}`}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1"
          >
            {uploading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {preview ? "Replace Image" : placeholder}
          </Button>
        </div>
        
        <p className="text-xs text-gray-400">Max 10MB. Supported: JPG, PNG, GIF, WebP</p>
      </div>
    </div>
  );
}

// Inline Confirmation Page Editor - Used at both Admin and Per-Invite levels
function InlineConfirmationEditor({ 
  settings, 
  onUpdate, 
  categoryPreset = null,
  showGatedContent = true,
  showVirtualEvent = true,
  compact = false 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [localSettings, setLocalSettings] = useState(settings || {});

  // Initialize with category preset defaults if no settings
  useEffect(() => {
    if (categoryPreset && !settings?.confirmation_title) {
      const preset = PILOT_CATEGORY_PRESETS[categoryPreset];
      if (preset?.defaultSettings) {
        setLocalSettings(prev => ({
          ...prev,
          ...preset.defaultSettings
        }));
      }
    }
  }, [categoryPreset, settings]);

  const updateField = (field, value) => {
    const updated = { ...localSettings, [field]: value };
    setLocalSettings(updated);
    onUpdate(updated);
  };

  // Compact inline view with click-to-edit
  if (!isEditing && compact) {
    return (
      <div 
        onClick={() => setIsEditing(true)}
        className="p-4 border rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 cursor-pointer hover:shadow-md transition-all group"
      >
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm text-gray-500">Confirmation Page Preview</h4>
          <Badge variant="outline" className="text-xs group-hover:bg-blue-100">Click to Edit</Badge>
        </div>
        <div className="text-center py-4">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500" />
          <h3 className="font-bold text-lg">{localSettings.confirmation_title || "You're Confirmed!"}</h3>
          <p className="text-sm text-gray-600">{localSettings.confirmation_subtitle || "Welcome"}</p>
          <p className="text-xs text-gray-500 mt-2">{localSettings.confirmation_message || "Thank you for confirming."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h4 className="font-medium flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          Confirmation Page
        </h4>
        {compact && (
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Header Fields - Inline Editable */}
      <div className="grid grid-cols-1 gap-3">
        <div className="relative group">
          <Label className="text-xs text-gray-500">Title</Label>
          <Input
            value={localSettings.confirmation_title || ""}
            onChange={(e) => updateField("confirmation_title", e.target.value)}
            placeholder="You're Confirmed!"
            className="font-semibold text-lg border-transparent hover:border-gray-200 focus:border-blue-500"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Subtitle</Label>
          <Input
            value={localSettings.confirmation_subtitle || ""}
            onChange={(e) => updateField("confirmation_subtitle", e.target.value)}
            placeholder="Welcome to the experience"
            className="border-transparent hover:border-gray-200 focus:border-blue-500"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Message</Label>
          <Textarea
            value={localSettings.confirmation_message || ""}
            onChange={(e) => updateField("confirmation_message", e.target.value)}
            placeholder="Thank you for confirming. Here's what you need to know..."
            rows={2}
            className="border-transparent hover:border-gray-200 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Gated Content Toggle & Settings */}
      {showGatedContent && (
        <div className="p-3 border rounded-lg bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-medium text-sm flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-600" />
                Gated Content
              </p>
              <p className="text-xs text-gray-500">Reveal after check-in</p>
            </div>
            <Switch
              checked={localSettings.gated_content_enabled || false}
              onCheckedChange={(checked) => updateField("gated_content_enabled", checked)}
            />
          </div>
          
          {localSettings.gated_content_enabled && (
            <div className="space-y-3 pt-3 border-t border-blue-200">
              <div>
                <Label className="text-xs">Pre-Checkin Message</Label>
                <Input
                  value={localSettings.gated_pre_checkin_message || ""}
                  onChange={(e) => updateField("gated_pre_checkin_message", e.target.value)}
                  placeholder="Check in to unlock..."
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Content After Check-in (Markdown)</Label>
                <Textarea
                  value={localSettings.gated_content_body || ""}
                  onChange={(e) => updateField("gated_content_body", e.target.value)}
                  placeholder="## Welcome!\n\nYour Zoom link: [Join](https://...)"
                  rows={4}
                  className="text-sm font-mono"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Virtual Event / Zoom Settings */}
      {showVirtualEvent && (
        <div className="p-3 border rounded-lg bg-indigo-50 border-indigo-200">
          <div className="flex items-center gap-2 mb-3">
            <Video className="w-4 h-4 text-indigo-600" />
            <p className="font-medium text-sm">Virtual Event</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Zoom/Meeting Link</Label>
              <Input
                value={localSettings.virtual_meeting_link || ""}
                onChange={(e) => updateField("virtual_meeting_link", e.target.value)}
                placeholder="https://zoom.us/j/..."
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input
                value={localSettings.virtual_meeting_password || ""}
                onChange={(e) => updateField("virtual_meeting_password", e.target.value)}
                placeholder="Optional"
                className="text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Join Button Text</Label>
              <Input
                value={localSettings.btn_join_meeting || ""}
                onChange={(e) => updateField("btn_join_meeting", e.target.value)}
                placeholder="Join Zoom Meeting"
                className="text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Live Preview */}
      <div className="p-4 border rounded-lg bg-gradient-to-br from-green-50 to-emerald-50">
        <p className="text-xs text-gray-500 mb-2">Live Preview</p>
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">
            {localSettings.confirmation_title || "You're Confirmed!"}
          </h3>
          <p className="text-gray-600 mb-2">
            {localSettings.confirmation_subtitle || "Welcome"}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {localSettings.confirmation_message || "Thank you for confirming."}
          </p>
          
          {localSettings.gated_content_enabled && !localSettings.checked_in && (
            <div className="bg-white/80 rounded-lg p-3 border text-left mb-3">
              <p className="text-sm text-gray-600">
                {localSettings.gated_pre_checkin_message || "Check in to unlock your content."}
              </p>
              <Button size="sm" className="mt-2 w-full">
                {localSettings.btn_checkin || "Check In"}
              </Button>
            </div>
          )}
          
          {localSettings.virtual_meeting_link && (
            <Button className="bg-blue-600 hover:bg-blue-700" size="sm">
              <Video className="w-4 h-4 mr-2" />
              {localSettings.btn_join_meeting || "Join Zoom Meeting"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Category icons and colors matching the screenshot
const CATEGORY_CONFIG = {
  pilots: { 
    icon: Sparkles, 
    color: "bg-purple-600", 
    textColor: "text-purple-600",
    bgLight: "bg-purple-50",
    borderColor: "border-purple-200"
  },
  investors: {
    icon: TrendingUp,
    color: "bg-blue-600",
    textColor: "text-blue-600",
    bgLight: "bg-blue-50",
    borderColor: "border-blue-200",
    description: "Angel investors, VCs, and strategic partners"
  },
  team: {
    icon: Users,
    color: "bg-indigo-600",
    textColor: "text-indigo-600",
    bgLight: "bg-indigo-50",
    borderColor: "border-indigo-200",
    description: "Core team, contractors, and advisors"
  },
  support: { 
    icon: Shield, 
    color: "bg-emerald-600", 
    textColor: "text-emerald-600",
    bgLight: "bg-emerald-50",
    borderColor: "border-emerald-200",
    description: "Team, Investors, Network, Clients portals"
  },
  integrations: { 
    icon: Plug, 
    color: "bg-black", 
    textColor: "text-black",
    bgLight: "bg-gray-100",
    borderColor: "border-gray-300"
  }
};

// Pilot Category Presets - Colors, Icons, and Default Settings
// NO EMOJIS - Use Lucide icon names as references
const PILOT_CATEGORY_PRESETS = {
  hair: {
    name: "Hair",
    iconName: "Scissors", // Lucide icon name
    color: "#ec4899", // Pink
    bgGradient: "from-pink-500 to-purple-500",
    bgLight: "bg-pink-50",
    textColor: "text-pink-600",
    borderColor: "border-pink-200",
    description: "Hair stylists and salon professionals",
    defaultSettings: {
      show_dietary_section: false,
      show_music_section: false,
      show_accessibility_section: true,
      nfc_kiosk_enabled: false,
      gated_content_enabled: false,
      confirmation_title: "You're Booked!",
      confirmation_subtitle: "See you at the salon",
      confirmation_message: "Your appointment is confirmed. Here's what to know:"
    }
  },
  nails: {
    name: "Nails",
    iconName: "Palette",
    color: "#f472b6", // Pink-400
    bgGradient: "from-pink-400 to-rose-500",
    bgLight: "bg-rose-50",
    textColor: "text-rose-600",
    borderColor: "border-rose-200",
    description: "Nail technicians and salon professionals",
    defaultSettings: {
      show_dietary_section: false,
      show_music_section: false,
      show_accessibility_section: true,
      nfc_kiosk_enabled: false,
      gated_content_enabled: false,
      confirmation_title: "Appointment Confirmed!",
      confirmation_subtitle: "Your nail session is booked",
      confirmation_message: "We can't wait to see you! Bring your inspo photos."
    }
  },
  makeup: {
    name: "Makeup",
    iconName: "Sparkles",
    color: "#f472b6",
    bgGradient: "from-pink-400 to-rose-500",
    bgLight: "bg-pink-50",
    textColor: "text-pink-600",
    borderColor: "border-pink-200",
    description: "Makeup artists and beauty professionals",
    defaultSettings: {
      show_dietary_section: false,
      show_music_section: false,
      show_accessibility_section: true,
      nfc_kiosk_enabled: false,
      gated_content_enabled: false,
      confirmation_title: "Glam Session Booked!",
      confirmation_subtitle: "Get ready to shine",
      confirmation_message: "Your makeup appointment is confirmed. Come with clean skin!"
    }
  },
  lash: {
    name: "Lash",
    iconName: "Eye",
    color: "#8b5cf6", // Violet
    bgGradient: "from-violet-500 to-purple-500",
    bgLight: "bg-violet-50",
    textColor: "text-violet-600",
    borderColor: "border-violet-200",
    description: "Lash technicians and extensions specialists",
    defaultSettings: {
      show_dietary_section: false,
      show_music_section: false,
      show_accessibility_section: true,
      nfc_kiosk_enabled: false,
      gated_content_enabled: false,
      confirmation_title: "Lash Appointment Set!",
      confirmation_subtitle: "Flutter-ready soon",
      confirmation_message: "Arrive with clean lashes, no makeup. Session takes 1-2 hours."
    }
  },
  wellness: {
    name: "Wellness",
    iconName: "Heart",
    color: "#10b981", // Emerald
    bgGradient: "from-emerald-500 to-teal-500",
    bgLight: "bg-emerald-50",
    textColor: "text-emerald-600",
    borderColor: "border-emerald-200",
    description: "Wellness practitioners and therapists",
    defaultSettings: {
      show_dietary_section: true,
      show_music_section: true,
      show_accessibility_section: true,
      nfc_kiosk_enabled: false,
      gated_content_enabled: false,
      confirmation_title: "Session Confirmed!",
      confirmation_subtitle: "Your wellness journey awaits",
      confirmation_message: "Wear comfortable clothing. Arrive 10 minutes early."
    }
  },
  events: {
    name: "Events",
    iconName: "PartyPopper",
    color: "#f59e0b", // Amber
    bgGradient: "from-amber-500 to-orange-500",
    bgLight: "bg-amber-50",
    textColor: "text-amber-600",
    borderColor: "border-amber-200",
    description: "Event organizers and party planners",
    defaultSettings: {
      show_dietary_section: true,
      show_music_section: true,
      show_accessibility_section: true,
      nfc_kiosk_enabled: true,
      gated_content_enabled: false,
      confirmation_title: "You're On The List!",
      confirmation_subtitle: "See you at the event",
      confirmation_message: "Your RSVP is confirmed. Show this page at check-in."
    }
  },
  instructor: {
    name: "Instructor",
    iconName: "GraduationCap",
    color: "#3b82f6", // Blue
    bgGradient: "from-blue-500 to-sky-500",
    bgLight: "bg-blue-50",
    textColor: "text-blue-600",
    borderColor: "border-blue-200",
    description: "Workshop leaders and educators",
    defaultSettings: {
      show_dietary_section: false,
      show_music_section: true,
      show_accessibility_section: true,
      nfc_kiosk_enabled: true,
      gated_content_enabled: true,
      confirmation_title: "You're Registered!",
      confirmation_subtitle: "Access your workshop materials",
      confirmation_message: "Check in below to unlock your Zoom link and resources.",
      gated_content_title: "Workshop Access",
      gated_pre_checkin_message: "Check in to unlock your workshop materials and Zoom link.",
      gated_content_body: "## Welcome to the Workshop!\n\nYour Zoom link and materials will appear here after check-in."
    }
  },
  investor: {
    name: "Investor",
    iconName: "TrendingUp",
    color: "#059669", // Emerald-600
    bgGradient: "from-emerald-600 to-green-500",
    bgLight: "bg-emerald-50",
    textColor: "text-emerald-600",
    borderColor: "border-emerald-200",
    description: "Investment partners and stakeholders",
    defaultSettings: {
      show_dietary_section: false,
      show_music_section: false,
      show_accessibility_section: false,
      nfc_kiosk_enabled: false,
      gated_content_enabled: true,
      confirmation_title: "Welcome, Investor!",
      confirmation_subtitle: "Access your investor portal",
      confirmation_message: "Thank you for your partnership. Access your dashboard below.",
      gated_content_title: "Investor Dashboard",
      gated_pre_checkin_message: "Verify your identity to access investor materials.",
      gated_content_body: "## Investor Portal\n\nAccess your investment dashboard, reports, and cap table."
    }
  },
  culinary: {
    name: "Culinary",
    iconName: "ChefHat",
    color: "#f97316", // Orange
    bgGradient: "from-orange-500 to-red-500",
    bgLight: "bg-orange-50",
    textColor: "text-orange-600",
    borderColor: "border-orange-200",
    description: "Chefs and culinary professionals",
    defaultSettings: {
      show_dietary_section: true,
      show_music_section: false,
      show_accessibility_section: true,
      nfc_kiosk_enabled: false,
      gated_content_enabled: false,
      confirmation_title: "Table Reserved!",
      confirmation_subtitle: "Your culinary experience awaits",
      confirmation_message: "Please share any dietary restrictions before your visit."
    }
  },
  dispensary: {
    name: "Dispensary",
    iconName: "Leaf",
    color: "#22c55e", // Green
    bgGradient: "from-green-500 to-emerald-500",
    bgLight: "bg-green-50",
    textColor: "text-green-600",
    borderColor: "border-green-200",
    description: "Cannabis dispensaries and wellness shops",
    defaultSettings: {
      show_dietary_section: false,
      show_music_section: false,
      show_accessibility_section: true,
      nfc_kiosk_enabled: false,
      gated_content_enabled: false,
      confirmation_title: "Order Confirmed!",
      confirmation_subtitle: "Ready for pickup",
      confirmation_message: "Bring valid ID. Your order will be ready at the scheduled time."
    }
  },
  travel: {
    name: "Travel",
    iconName: "Plane",
    color: "#0ea5e9", // Sky
    bgGradient: "from-sky-500 to-blue-500",
    bgLight: "bg-sky-50",
    textColor: "text-sky-600",
    borderColor: "border-sky-200",
    description: "Travel agents and hospitality",
    defaultSettings: {
      show_dietary_section: true,
      show_music_section: false,
      show_accessibility_section: true,
      nfc_kiosk_enabled: false,
      gated_content_enabled: true,
      confirmation_title: "Trip Confirmed!",
      confirmation_subtitle: "Your adventure begins",
      confirmation_message: "Check in to access your full itinerary and travel documents."
    }
  },
  retail: {
    name: "Retail",
    iconName: "ShoppingBag",
    color: "#06b6d4", // Cyan
    bgGradient: "from-cyan-500 to-teal-500",
    bgLight: "bg-cyan-50",
    textColor: "text-cyan-600",
    borderColor: "border-cyan-200",
    description: "Retail stores and boutiques",
    defaultSettings: {
      show_dietary_section: false,
      show_music_section: false,
      show_accessibility_section: true,
      nfc_kiosk_enabled: true,
      gated_content_enabled: false,
      confirmation_title: "Appointment Set!",
      confirmation_subtitle: "Personal shopping awaits",
      confirmation_message: "Your stylist is ready to help you find the perfect look."
    }
  }
};

export default function PrivatePortalsSection({ embedded = false }) {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState("pilots");
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [subCategoryData, setSubCategoryData] = useState(null);
  const [activeTab, setActiveTab] = useState("members");
  
  // Dialogs
  const [showAddSubCategory, setShowAddSubCategory] = useState(false);
  const [showPortalConfigurator, setShowPortalConfigurator] = useState(false);
  const [editingPortalConfig, setEditingPortalConfig] = useState(null);
  const [showAddInvite, setShowAddInvite] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrefillSection, setShowPrefillSection] = useState(false);
  const [showJourneyDialog, setShowJourneyDialog] = useState(false);
  const [journeyPerson, setJourneyPerson] = useState(null); // The member or invite to show journey for
  
  // Form states
  const [newSubCategory, setNewSubCategory] = useState({ name: "", description: "" });
  const [newInvite, setNewInvite] = useState({ 
    name: "", 
    email: "", 
    company_name: "",
    // Pre-fill intake fields
    prefill: {
      // Discovery
      instagram: "",
      tiktok: "",
      facebook: "",
      website: "",
      google_business: "",
      yelp: "",
      styleseat: "",
      booksy: "",
      // Booking
      booking_platforms: [],
      payment_methods: [],
      // Business info
      location: "",
      years_experience: "",
      services_offered: "",
      pricing_range: "",
      // Tech stack
      tech_tools: [],
      // Notes
      admin_notes: ""
    },
    // Per-invite confirmation page overrides
    confirmation_settings: {}
  });
  const [showConfirmationEditor, setShowConfirmationEditor] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ users: [], companies: [] });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  const [creatingSubCategory, setCreatingSubCategory] = useState(false);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  
  // Edit/Delete invite states
  const [editingInvite, setEditingInvite] = useState(null);
  const [editInviteData, setEditInviteData] = useState({ name: "", email: "", company_name: "" });
  const [deletingInvite, setDeletingInvite] = useState(null);
  const [savingInvite, setSavingInvite] = useState(false);
  
  // Edit/Delete portal (sub-category) states
  const [editingPortal, setEditingPortal] = useState(null);
  const [editPortalData, setEditPortalData] = useState({ name: "", description: "" });
  const [deletingPortal, setDeletingPortal] = useState(null);
  const [savingPortal, setSavingPortal] = useState(false);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch sub-category details
  const fetchSubCategoryDetails = useCallback(async (categoryType, subCatId) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories/${categoryType}/sub-categories/${subCatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubCategoryData(data);
      }
    } catch (err) {
      console.error("Failed to fetch sub-category:", err);
    }
  }, []);

  useEffect(() => {
    if (selectedSubCategory) {
      fetchSubCategoryDetails(expandedCategory, selectedSubCategory.sub_category_id);
    }
  }, [selectedSubCategory, expandedCategory, fetchSubCategoryDetails]);

  // Search users/companies
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults({ users: [], companies: [] });
      return;
    }
    
    try {
      const token = localStorage.getItem("session_token");
      const [usersRes, companiesRes] = await Promise.all([
        fetch(`${API}/admin/portal-categories/search-users?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/portal-categories/search-companies?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const users = usersRes.ok ? (await usersRes.json()).users : [];
      const companies = companiesRes.ok ? (await companiesRes.json()).companies : [];
      
      setSearchResults({ users, companies });
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  // Create sub-category
  const handleCreateSubCategory = async () => {
    setCreatingSubCategory(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories/${expandedCategory}/sub-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newSubCategory)
      });
      
      if (res.ok) {
        toast.success("Sub-category created!");
        setShowAddSubCategory(false);
        setNewSubCategory({ name: "", description: "" });
        fetchCategories();
      } else {
        toast.error("Failed to create sub-category");
      }
    } catch (err) {
      toast.error("Error creating sub-category");
    } finally {
      setCreatingSubCategory(false);
    }
  };

  // Create invite
  const handleCreateInvite = async () => {
    if (!selectedSubCategory) return;
    
    setCreatingInvite(true);
    try {
      const token = localStorage.getItem("session_token");
      const payload = {
        ...newInvite,
        existing_user_id: selectedUser?.user_id,
        company_id: selectedCompany?.company_id
      };
      
      const res = await fetch(
        `${API}/admin/portal-categories/${expandedCategory}/sub-categories/${selectedSubCategory.sub_category_id}/invite`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        setInviteUrl(data.invite?.invite_url || "");
        toast.success("Invite created!");
        fetchSubCategoryDetails(expandedCategory, selectedSubCategory.sub_category_id);
      } else {
        toast.error("Failed to create invite");
      }
    } catch (err) {
      toast.error("Error creating invite");
    } finally {
      setCreatingInvite(false);
    }
  };

  const copyInviteUrl = () => {
    navigator.clipboard.writeText(inviteUrl);
    toast.success("Invite URL copied!");
  };

  const resetInviteForm = () => {
    setNewInvite({ 
      name: "", 
      email: "", 
      company_name: "",
      prefill: {
        instagram: "",
        tiktok: "",
        facebook: "",
        website: "",
        google_business: "",
        yelp: "",
        styleseat: "",
        booksy: "",
        booking_platforms: [],
        payment_methods: [],
        location: "",
        years_experience: "",
        services_offered: "",
        pricing_range: "",
        tech_tools: [],
        admin_notes: ""
      },
      confirmation_settings: {}
    });
    setSelectedUser(null);
    setSelectedCompany(null);
    setSearchQuery("");
    setSearchResults({ users: [], companies: [] });
    setInviteUrl("");
    setShowAddInvite(false);
    setShowPrefillSection(false);
    setShowConfirmationEditor(false);
  };

  // Edit invite handler
  const handleEditInvite = (invite) => {
    setEditingInvite(invite);
    setEditInviteData({
      name: invite.name || "",
      email: invite.email || "",
      company_name: invite.company_name || ""
    });
  };

  const handleSaveInvite = async () => {
    if (!editingInvite) return;
    setSavingInvite(true);
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-invites/${editingInvite.invite_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editInviteData)
      });
      
      if (res.ok) {
        toast.success("Invite updated!");
        setEditingInvite(null);
        // Refresh data
        if (selectedSubCategory) {
          fetchSubCategoryDetails(expandedCategory, selectedSubCategory.sub_category_id);
        }
      } else {
        toast.error("Failed to update invite");
      }
    } catch (err) {
      toast.error("Error updating invite");
    } finally {
      setSavingInvite(false);
    }
  };

  // Delete invite handler
  const handleDeleteInvite = async (invite) => {
    if (!invite) return;
    setDeletingInvite(invite);
  };

  const confirmDeleteInvite = async () => {
    if (!deletingInvite) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-invites/${deletingInvite.invite_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Invite deleted!");
        setDeletingInvite(null);
        // Refresh data
        if (selectedSubCategory) {
          fetchSubCategoryDetails(expandedCategory, selectedSubCategory.sub_category_id);
        }
      } else {
        toast.error("Failed to delete invite");
      }
    } catch (err) {
      toast.error("Error deleting invite");
    }
  };

  // View journey for a member or invite
  const handleViewJourney = (person, type) => {
    setJourneyPerson({ ...person, type });
    setShowJourneyDialog(true);
  };

  // Delete member handler
  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.user_name || member.user_email}?`)) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-members/${member.member_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Member removed!");
        // Refresh data
        if (selectedSubCategory) {
          fetchSubCategoryDetails(expandedCategory, selectedSubCategory.sub_category_id);
        }
      } else {
        toast.error("Failed to remove member");
      }
    } catch (err) {
      toast.error("Error removing member");
    }
  };

  // Open portal edit with full configurator
  const handleEditPortal = (portal) => {
    setEditingPortalConfig(portal);
    setShowPortalConfigurator(true);
  };
  
  // Legacy edit handler (keeping for backward compat with simple edit)
  const handleEditPortalLegacy = (portal) => {
    setEditPortalData({
      name: portal.name || "",
      description: portal.description || ""
    });
    setEditingPortal(portal);
  };

  // Save portal handler
  const handleSavePortal = async () => {
    if (!editingPortal) return;
    setSavingPortal(true);
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories/${expandedCategory}/sub-categories/${editingPortal.sub_category_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editPortalData)
      });
      
      if (res.ok) {
        toast.success("Portal updated!");
        setEditingPortal(null);
        fetchCategories();
        if (selectedSubCategory?.sub_category_id === editingPortal.sub_category_id) {
          setSelectedSubCategory({ ...selectedSubCategory, ...editPortalData });
        }
      } else {
        toast.error("Failed to update portal");
      }
    } catch (err) {
      toast.error("Error updating portal");
    } finally {
      setSavingPortal(false);
    }
  };

  // Delete portal handler
  const handleDeletePortal = (portal) => {
    setDeletingPortal(portal);
  };

  const confirmDeletePortal = async () => {
    if (!deletingPortal) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories/${expandedCategory}/sub-categories/${deletingPortal.sub_category_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Portal deleted!");
        setDeletingPortal(null);
        // Clear selection if deleted portal was selected
        if (selectedSubCategory?.sub_category_id === deletingPortal.sub_category_id) {
          setSelectedSubCategory(null);
          setSubCategoryData(null);
        }
        fetchCategories();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to delete portal");
      }
    } catch (err) {
      toast.error("Error deleting portal");
    }
  };

  // Get status badge
  const getStatusBadge = (status, member = {}) => {
    // Handle all possible status values
    if (member.intake_completed || status === "intake_complete" || status === "intake_completed") {
      return <Badge variant="default" className="bg-orange-500">Intake Done</Badge>;
    }
    if (member.nda_signed || status === "nda_signed") {
      return <Badge variant="default" className="bg-purple-500">NDA Signed</Badge>;
    }
    const statusConfig = {
      pending: { label: "Pending", variant: "secondary" },
      invite_accepted: { label: "Accepted", variant: "default", className: "bg-blue-500" },
      accepted: { label: "Accepted", variant: "default", className: "bg-blue-500" },
      pending_nda: { label: "Pending NDA", variant: "default", className: "bg-blue-500" },
      intake: { label: "In Intake", variant: "default", className: "bg-purple-500" },
      active: { label: "Active", variant: "default", className: "bg-green-500" },
      complete: { label: "Complete", variant: "default", className: "bg-green-500" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 flex justify-center items-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "p-4 md:p-8"}>
      {/* Header - only show when not embedded in Super Admin */}
      {!embedded && (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">Host Dashboards</h1>
          <p className="text-sm text-gray-500">Manage templates, presets, and pilot portals for all categories</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm("This will reset ALL intake templates to the latest defaults (44 questions each). Continue?")) return;
              try {
                const token = localStorage.getItem("session_token");
                const res = await fetch(`${API}/admin/reset-templates`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                if (res.ok) {
                  toast.success(`Templates reset! ${data.seeded} categories reseeded with 44 questions each.`);
                  // Refresh the page data
                  setLoading(true);
                  fetchCategories().finally(() => setLoading(false));
                } else {
                  toast.error(data.detail || "Failed to reset templates");
                }
              } catch (err) {
                toast.error("Failed to reset templates");
              }
            }}
            className="flex items-center gap-2 text-orange-600 border-orange-300 hover:bg-orange-50"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Templates
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setLoading(true);
              fetchCategories().finally(() => setLoading(false));
              if (selectedSubCategory) {
                fetchSubCategoryDetails(expandedCategory, selectedSubCategory.sub_category_id);
              }
              toast.success("Data refreshed!");
            }}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>
      )}

      {/* Section Tabs - When embedded, skip tabs and show Pilot Portals directly */}
      {embedded ? (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Pilot Portal Control</h3>
              <p className="text-sm text-gray-500">Manage invites, NDAs, intakes, and onboarding per category</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchCategories().finally(() => setLoading(false)); }}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>
      ) : (
        <Tabs defaultValue="pilot-portals" className="mb-6">
        <TabsList>
          <TabsTrigger value="templates" data-testid="host-dash-templates-tab">Templates</TabsTrigger>
          <TabsTrigger value="presets" data-testid="host-dash-presets-tab">Presets</TabsTrigger>
          <TabsTrigger value="pilot-portals" data-testid="host-dash-pilots-tab">Pilot Portals</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4">
          <div className="border rounded-lg p-6 bg-white" data-testid="templates-section">
            <h3 className="text-lg font-semibold mb-2">Dashboard Templates</h3>
            <p className="text-sm text-gray-500 mb-4">Category-based dashboard templates for all 11 service types. Each template defines the default layout, intake forms, and settings for host dashboards in that category.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {categories.filter(c => c.type !== 'team').map(cat => {
                const cfg = CATEGORY_CONFIG[cat.type];
                const Ic = cfg?.icon || Sparkles;
                return (
                  <button
                    key={cat.type}
                    onClick={() => {
                      setExpandedCategory(cat.type);
                      setSelectedSubCategory(null);
                      const tabs = document.querySelector('[data-testid="host-dash-pilots-tab"]');
                      if (tabs) tabs.click();
                    }}
                    className="flex items-center gap-2 p-3 border rounded-lg hover:bg-gray-50 transition-colors text-left"
                    data-testid={`template-${cat.type}`}
                  >
                    <div className={`w-8 h-8 rounded-lg ${cfg?.color || 'bg-gray-500'} text-white flex items-center justify-center shrink-0`}>
                      <Ic className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium capitalize">{cat.type}</p>
                      <p className="text-xs text-gray-400">{cat.sub_categories?.length || 0} pilots</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="presets" className="mt-4">
          <div className="border rounded-lg p-6 bg-white" data-testid="presets-section">
            <h3 className="text-lg font-semibold mb-2">Default Presets</h3>
            <p className="text-sm text-gray-500 mb-4">Global default settings applied to new host dashboards. These presets control default scheduling, pricing, intake form layouts, and notification preferences.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium">Intake Form Defaults</p>
                <p className="text-xs text-gray-500 mt-1">44 standard questions per category, 9 sections</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => {
                  const tabs = document.querySelector('[data-testid="host-dash-pilots-tab"]');
                  if (tabs) tabs.click();
                }}>Manage Templates</Button>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium">Scheduling Presets</p>
                <p className="text-xs text-gray-500 mt-1">Default availability windows, buffer times, and booking rules</p>
                <Badge variant="outline" className="mt-3">Coming Soon</Badge>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium">Pricing Presets</p>
                <p className="text-xs text-gray-500 mt-1">Default service pricing ranges and currency settings</p>
                <Badge variant="outline" className="mt-3">Coming Soon</Badge>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium">Notification Presets</p>
                <p className="text-xs text-gray-500 mt-1">Default email/SMS notification triggers for hosts</p>
                <Badge variant="outline" className="mt-3">Coming Soon</Badge>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pilot-portals" className="mt-4">
        </TabsContent>
      </Tabs>
      )}

      {/* Main Pilot Portals Layout - shared by both embedded and full modes */}
      <div className={`flex flex-col lg:flex-row gap-6 ${!selectedSubCategory ? '' : ''}`}>
        {/* Left: Categories List */}
        <div className={`space-y-3 flex-shrink-0 ${selectedSubCategory ? 'w-full lg:w-80' : 'w-full'}`}>
          <div className={selectedSubCategory ? '' : (embedded ? '' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3')}>
          {categories.filter(c => {
            if (c.type === 'team') return false;
            // In embedded mode (Super Admin Pilot Onboarding), only show pilots
            if (embedded && c.type !== 'pilots') return false;
            return true;
          }).map((category) => {
            const config = CATEGORY_CONFIG[category.type];
            const Icon = config?.icon || Sparkles;
            const isExpanded = expandedCategory === category.type;
            
            return (
              <div key={category.type} className="border rounded-lg overflow-hidden bg-white">
                {/* Category Header */}
                <button
                  onClick={() => {
                    setExpandedCategory(isExpanded ? null : category.type);
                    setSelectedSubCategory(null);
                    setSubCategoryData(null);
                  }}
                  className={`w-full p-3 md:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                    isExpanded ? config?.bgLight : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg ${config?.color} text-white flex items-center justify-center`}>
                      <Icon className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium capitalize text-sm md:text-base">{category.type}</p>
                      <p className="text-xs text-gray-500">
                        {category.total_sub_categories} portal{category.total_sub_categories !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {/* Sub-categories */}
                {isExpanded && (
                  <div className="border-t">
                    {category.sub_categories.length === 0 ? (
                      <p className="p-3 md:p-4 text-sm text-gray-500 text-center">
                        No portals yet
                      </p>
                    ) : (
                      <div className="divide-y">
                        {category.sub_categories.map((sub) => (
                          <div
                            key={sub.sub_category_id}
                            className={`p-3 hover:bg-gray-50 transition-colors ${
                              selectedSubCategory?.sub_category_id === sub.sub_category_id ? "bg-gray-100" : ""
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() => {
                                  if (category.type === "pilots") {
                                    const categoryName = sub.name?.toLowerCase().replace(/\s+/g, '-') || 'default';
                                    navigate(`/admin/portals/pilots/${categoryName}/overview?id=${sub.sub_category_id}`);
                                  } else {
                                    setSelectedSubCategory(sub);
                                  }
                                }}
                                className="flex-1 text-left"
                              >
                                <p className="font-medium text-sm">{sub.name}</p>
                                <p className="text-xs text-gray-500">{sub.member_count || 0} members</p>
                              </button>
                              <div className="flex items-center gap-1">
                                {/* Open Dashboard Button - Only for pilots category */}
                                {category.type === "pilots" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const categoryName = sub.name?.toLowerCase().replace(/\s+/g, '-') || 'default';
                                      navigate(`/admin/portals/pilots/${categoryName}/overview?id=${sub.sub_category_id}`);
                                    }}
                                    title="Open Dashboard"
                                    data-testid={`open-dashboard-${sub.sub_category_id}`}
                                  >
                                    <LayoutDashboard className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditPortal(sub);
                                  }}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePortal(sub);
                                  }}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add Portal Button */}
                    <button
                      onClick={() => {
                        setEditingPortalConfig(null);
                        setShowPortalConfigurator(true);
                      }}
                      className={`w-full p-3 text-sm ${config?.textColor} hover:bg-gray-50 flex items-center gap-2 border-t`}
                    >
                      <Plus className="w-4 h-4" />
                      Add Portal
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>

        {/* Right: Selected Sub-Category Details - only show when a subcategory is selected */}
        {selectedSubCategory && (
        <div className="flex-1 min-w-0">
          {selectedSubCategory && subCategoryData ? (
            <Card className="h-full">
              <CardHeader className="border-b pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg md:text-xl">{selectedSubCategory.name}</CardTitle>
                    <CardDescription className="capitalize">{expandedCategory}</CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Portal Status Selector - DRAFT/TESTING/LIVE */}
                    <PortalStatusSelector 
                      portalId={selectedSubCategory?.portal_id || selectedSubCategory?.sub_category_id}
                      currentStatus={selectedSubCategory?.status || "draft"}
                      onStatusChange={(newStatus) => {
                        // Update local state when status changes
                        setSelectedSubCategory(prev => ({...prev, status: newStatus}));
                      }}
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAddInvite(true)}
                    >
                      <User className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Add User</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-0">
                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 overflow-x-auto">
                    <TabsTrigger 
                      value="status" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent px-4 py-3 text-sm whitespace-nowrap"
                    >
                      Status Overview
                    </TabsTrigger>
                    <TabsTrigger 
                      value="members" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent px-4 py-3 text-sm whitespace-nowrap"
                    >
                      Members ({subCategoryData.members?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger 
                      value="pending" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent px-4 py-3 text-sm whitespace-nowrap"
                    >
                      Pending ({subCategoryData.pending_invites?.length || 0})
                    </TabsTrigger>
                    {expandedCategory !== "support" && (
                    <TabsTrigger 
                      value="intake" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent px-4 py-3 text-sm whitespace-nowrap"
                    >
                      Intake Responses
                    </TabsTrigger>
                    )}
                    {expandedCategory !== "support" && (
                    <TabsTrigger 
                      value="nda" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent px-4 py-3 text-sm whitespace-nowrap"
                    >
                      NDA Signatures
                    </TabsTrigger>
                    )}
                    <TabsTrigger 
                      value="settings" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent px-4 py-3 text-sm whitespace-nowrap"
                    >
                      Settings & Preview
                    </TabsTrigger>
                    <TabsTrigger 
                      value="advocate" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-amber-600 data-[state=active]:bg-transparent px-4 py-3 text-sm whitespace-nowrap"
                      data-testid="portal-advocate-tab"
                    >
                      Advocate
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Status Overview Tab - Shows all invites and their stage */}
                  <TabsContent value="status" className="m-0 p-4">
                    <PortalStatusOverview 
                      members={subCategoryData.members || []}
                      pendingInvites={subCategoryData.pending_invites || []}
                      subCategoryName={selectedSubCategory.name}
                      onDeleteParticipant={async (id, type) => {
                        if (type === "invite") {
                          const invite = subCategoryData.pending_invites?.find(i => i.invite_id === id);
                          if (invite) {
                            await handleDeleteInvite(invite);
                          }
                        } else if (type === "member") {
                          // Delete member
                          try {
                            const token = localStorage.getItem("session_token");
                            const res = await fetch(`${API}/admin/portal-members/${id}`, {
                              method: "DELETE",
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.ok) {
                              toast.success("Member removed successfully");
                              // Refresh the sub-category data
                              fetchSubCategoryData(selectedSubCategory.id);
                            } else {
                              toast.error("Failed to delete member");
                            }
                          } catch (err) {
                            toast.error("Error deleting member");
                          }
                        }
                      }}
                    />
                  </TabsContent>
                  
                  {/* Members Tab */}
                  <TabsContent value="members" className="m-0 p-4">
                    {subCategoryData.members?.length === 0 ? (
                      <div className="text-center py-12">
                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="font-medium">No members yet</p>
                        <p className="text-sm text-gray-500 mt-1">Create an invite to add members</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {subCategoryData.members?.map((member) => (
                          <div 
                            key={member.member_id}
                            className="p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                {/* Profile Picture */}
                                {member.user_picture ? (
                                  <img 
                                    src={member.user_picture} 
                                    alt={member.user_name || "User"}
                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">
                                    {(member.user_name || member.company_name || "U").charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="font-medium text-sm md:text-base truncate">
                                    {member.user_name || member.company_name || "Unknown"}
                                  </p>
                                  <p className="text-xs md:text-sm text-gray-500 truncate">{member.user_email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {getStatusBadge(member.status, member)}
                                {member.company_name && (
                                  <Badge variant="outline" className="text-xs">
                                    <Building2 className="w-3 h-3 mr-1" />
                                    {member.company_name}
                                  </Badge>
                                )}
                                {/* Action buttons */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewJourney(member, 'member')}
                                  title="View journey stages"
                                >
                                  <Map className="w-4 h-4 text-purple-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteMember(member)}
                                  title="Remove member"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Linked Experiences */}
                            {member.linked_experiences?.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs text-gray-500 mb-2">Linked Experiences:</p>
                                <div className="flex flex-wrap gap-2">
                                  {member.linked_experiences.map((exp, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {exp.name} ({exp.category_type})
                                      {getStatusBadge(exp.status, exp)}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Pending Invites Tab */}
                  <TabsContent value="pending" className="m-0 p-4">
                    {subCategoryData.pending_invites?.length === 0 ? (
                      <div className="text-center py-12">
                        <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="font-medium">No pending invites</p>
                        <p className="text-sm text-gray-500 mt-1">All invites have been accepted</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {subCategoryData.pending_invites?.map((invite) => (
                          <div 
                            key={invite.invite_id}
                            className="p-3 md:p-4 border rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{invite.name || invite.email}</p>
                              <p className="text-xs text-gray-500 truncate">{invite.email}</p>
                              {invite.company_name && (
                                <p className="text-xs text-gray-400 truncate">{invite.company_name}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 flex-wrap">
                              <Badge variant="secondary" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewJourney(invite, 'invite')}
                                title="View journey stages"
                              >
                                <Map className="w-4 h-4 text-purple-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    `${window.location.origin}/portal/invite/${invite.magic_token}`
                                  );
                                  toast.success("Invite link copied!");
                                }}
                                title="Copy invite link"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditInvite(invite)}
                                title="Edit invite"
                              >
                                <Edit2 className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteInvite(invite)}
                                title="Delete invite"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Intake Responses Tab - View pilot intake data */}
                  <TabsContent value="intake" className="m-0 p-4">
                    <IntakeResponsesViewer 
                      members={subCategoryData.members || []}
                      pendingInvites={subCategoryData.pending_invites || []}
                    />
                  </TabsContent>
                  
                  {/* NDA Signatures Tab */}
                  <TabsContent value="nda" className="m-0 p-4">
                    <NDASignaturesViewer 
                      members={subCategoryData.members || []}
                      portalId={selectedSubCategory?.portal_id}
                      subCategoryId={selectedSubCategory?.sub_category_id}
                    />
                  </TabsContent>
                  
                  {/* Settings & Preview Tab */}
                  <TabsContent value="settings" className="m-0 p-4">
                    <PortalSettingsWithPreview 
                      subCategory={subCategoryData.sub_category}
                      categoryType={expandedCategory}
                      subCategoryId={selectedSubCategory.sub_category_id}
                      onSave={async (settings) => {
                        try {
                          const token = localStorage.getItem("session_token");
                          const res = await fetch(
                            `${API}/admin/portal-categories/${expandedCategory}/sub-categories/${selectedSubCategory.sub_category_id}/settings`,
                            {
                              method: "PUT",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`
                              },
                              body: JSON.stringify({ settings })
                            }
                          );
                          if (res.ok) {
                            toast.success("Settings saved!");
                            fetchSubCategoryDetails(expandedCategory, selectedSubCategory.sub_category_id);
                          }
                        } catch (err) {
                          toast.error("Failed to save settings");
                        }
                      }}
                    />
                  </TabsContent>

                  {/* Advocate Tab - universal for all portal types */}
                  <TabsContent value="advocate" className="m-0 p-4">
                    <PortalAdvocateSettings
                      subCategory={subCategoryData.sub_category}
                      categoryType={expandedCategory}
                      subCategoryId={selectedSubCategory.sub_category_id}
                      members={subCategoryData.members || []}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : null}
        </div>
        )}
      </div>

      {/* Add Sub-Category Dialog */}
      <Dialog open={showAddSubCategory} onOpenChange={setShowAddSubCategory}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="capitalize">Add {expandedCategory} Portal</DialogTitle>
            <DialogDescription>Create a new portal within this category</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Portal Name</Label>
              <Input
                value={newSubCategory.name}
                onChange={(e) => setNewSubCategory(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Hair, Angel Investors, Contractors"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newSubCategory.description}
                onChange={(e) => setNewSubCategory(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this portal"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowAddSubCategory(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSubCategory} 
              disabled={creatingSubCategory || !newSubCategory.name}
              className="w-full sm:w-auto"
            >
              {creatingSubCategory ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Portal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW: Portal Configurator Modal (Comprehensive) */}
      <PortalConfiguratorModal
        isOpen={showPortalConfigurator}
        onClose={() => {
          setShowPortalConfigurator(false);
          setEditingPortalConfig(null);
        }}
        parentCategory={expandedCategory}
        existingPortal={editingPortalConfig}
        onSuccess={() => {
          fetchCategories();
          if (selectedSubCategory) {
            fetchSubCategoryDetails(expandedCategory, selectedSubCategory.sub_category_id);
          }
        }}
      />

      {/* Add Invite Dialog */}
      <Dialog open={showAddInvite} onOpenChange={(open) => !open && resetInviteForm()}>
        <DialogContent className="max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invite</DialogTitle>
            <DialogDescription>
              Search for existing users or create a new invite
            </DialogDescription>
          </DialogHeader>
          
          {inviteUrl ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Invite created successfully!
                </p>
                
                {/* Context-aware Journey Links */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 font-medium mb-2">Admin Preview - Click to view each stage:</p>
                  
                  {/* Stage 1: Invite Page - universal */}
                  <div className="flex items-center gap-2 p-2 bg-white rounded border">
                    <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">1</div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-xs font-medium">Invite Page</p>
                      <p className="text-[10px] text-gray-400 truncate">{inviteUrl}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => window.open(inviteUrl, '_blank')}>
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success("Copied!"); }}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Detect if this is an investor-type portal (under support category) */}
                  {(expandedCategory === "investors" || selectedSubCategory?.name?.toLowerCase()?.includes('investor') || selectedSubCategory?.preset_type === 'investors') ? (
                    <>
                      {/* Investor-specific journey */}
                      <div className="flex items-center gap-2 p-2 bg-white rounded border">
                        <div className="w-5 h-5 rounded-full bg-purple-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">2</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">Prefilled Info & NDA</p>
                          <p className="text-[10px] text-gray-400">Review prefilled details, sign NDA</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                          window.open(`${inviteUrl.replace('/portal/invite/', '/portal/nda/')}`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded border border-emerald-200">
                        <div className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">3</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-emerald-700">Investor Portal</p>
                          <p className="text-[10px] text-emerald-500">Projections, pitch deck, advocate earnings</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0 border-emerald-300" onClick={() => {
                          window.open(`${window.location.origin}/investor`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                    </>
                  ) : expandedCategory === "support" ? (
                    <>
                      {/* Support portal journey */}
                      <div className="flex items-center gap-2 p-2 bg-white rounded border">
                        <div className="w-5 h-5 rounded-full bg-purple-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">2</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">Welcome & Access</p>
                          <p className="text-[10px] text-gray-400">Welcome page with portal access</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                          window.open(`${inviteUrl.replace('/portal/invite/', '/portal/welcome/')}`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">3</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-blue-700">Support Portal</p>
                          <p className="text-[10px] text-blue-500">Team/investor/network views</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0 border-blue-300" onClick={() => {
                          window.open(`${window.location.origin}/support`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Pilot journey - full onboarding */}
                      <div className="flex items-center gap-2 p-2 bg-white rounded border">
                        <div className="w-5 h-5 rounded-full bg-purple-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">2</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">Welcome Page</p>
                          <p className="text-[10px] text-gray-400">After accepting invite</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                          window.open(`${inviteUrl.replace('/portal/invite/', '/portal/welcome/')}`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-white rounded border">
                        <div className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">3</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">NDA/Agreement</p>
                          <p className="text-[10px] text-gray-400">Sign partnership agreement</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                          window.open(`${inviteUrl.replace('/portal/invite/', '/portal/nda/')}`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-white rounded border">
                        <div className="w-5 h-5 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">4</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">Intake Form</p>
                          <p className="text-[10px] text-gray-400">Fill out profile details</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                          window.open(`${inviteUrl.replace('/portal/invite/', '/portal/intake/')}`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-white rounded border">
                        <div className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">5</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">Portal Dashboard</p>
                          <p className="text-[10px] text-gray-400">Final destination</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                          window.open(`${window.location.origin}/front-row`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-purple-50 rounded border border-purple-200">
                        <div className="w-5 h-5 rounded-full bg-purple-600 text-white text-[10px] flex items-center justify-center font-bold shrink-0">★</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-purple-700">Category Demo Page</p>
                          <p className="text-[10px] text-purple-500">Full demo for this category type</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0 border-purple-300" onClick={() => {
                          const categoryType = selectedSubCategory?.name?.toLowerCase() || expandedCategory || 'hair';
                          const categoryMap = {
                            'hair': '/hair', 'makeup': '/mua', 'nail': '/nail', 'nails': '/nail',
                            'wellness': '/wellness', 'event': '/events', 'events': '/events',
                            'instructor': '/instructor', 'culinary': '/culinary'
                          };
                          window.open(`${window.location.origin}${categoryMap[categoryType] || '/hair'}`, '_blank');
                        }}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Advocate Portal - universal for all portal types */}
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded border border-amber-200 mt-1">
                    <div className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold shrink-0">$</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-amber-700">Advocate Dashboard</p>
                      <p className="text-[10px] text-amber-500">Referral tracking, commission earnings</p>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0 border-amber-300" onClick={() => {
                      window.open(`${window.location.origin}/advocate`, '_blank');
                    }}>
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success("Main invite URL copied!"); }} variant="outline" className="flex-1">
                  <Copy className="w-4 h-4 mr-1" />
                  Copy Invite Link
                </Button>
                <Button onClick={resetInviteForm} className="flex-1">Done</Button>
              </div>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="space-y-4">
                <div>
                  <Label>Search existing users or companies</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Search by name, email, or company"
                      className="pl-10"
                    />
                  </div>
                </div>
                
                {/* Search Results */}
                {(searchResults.users.length > 0 || searchResults.companies.length > 0) && (
                  <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                    {searchResults.users.map(user => (
                      <button
                        key={user.user_id}
                        onClick={() => {
                          setSelectedUser(user);
                          setNewInvite(prev => ({
                            ...prev,
                            name: user.name || "",
                            email: user.email || "",
                            company_name: user.business_name || ""
                          }));
                        }}
                        className={`w-full p-3 text-left hover:bg-gray-50 flex items-center gap-2 ${
                          selectedUser?.user_id === user.user_id ? "bg-purple-50" : ""
                        }`}
                      >
                        <User className="w-4 h-4 text-gray-400" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{user.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                      </button>
                    ))}
                    {searchResults.companies.map(company => (
                      <button
                        key={company.company_id}
                        onClick={() => {
                          setSelectedCompany(company);
                          setNewInvite(prev => ({ ...prev, company_name: company.name }));
                        }}
                        className={`w-full p-3 text-left hover:bg-gray-50 flex items-center gap-2 ${
                          selectedCompany?.company_id === company.company_id ? "bg-purple-50" : ""
                        }`}
                      >
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <p className="text-sm font-medium truncate">{company.name}</p>
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">Or create new</span>
                  </div>
                </div>
                
                {/* Manual Entry */}
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newInvite.name}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newInvite.email}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label>Company / Business Name</Label>
                  <Input
                    value={newInvite.company_name}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Company name (for linking)"
                  />
                </div>
                
                {/* Pre-fill Intake Toggle */}
                <button
                  type="button"
                  onClick={() => setShowPrefillSection(!showPrefillSection)}
                  className="w-full flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">Pre-fill Intake Data</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-purple-600 transition-transform ${showPrefillSection ? "rotate-180" : ""}`} />
                </button>
                
                {/* Pre-fill Section */}
                {showPrefillSection && (
                  <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                    <p className="text-xs text-gray-500 mb-3">
                      Pre-fill info you already know. Provider will review and add more.
                    </p>
                    
                    {/* Social Media */}
                    <div className="space-y-3">
                      <Label className="text-xs font-semibold text-gray-700 uppercase">Social Media</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Instagram</Label>
                          <Input
                            value={newInvite.prefill?.instagram || ""}
                            onChange={(e) => setNewInvite(prev => ({ 
                              ...prev, 
                              prefill: { ...prev.prefill, instagram: e.target.value }
                            }))}
                            placeholder="@handle"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">TikTok</Label>
                          <Input
                            value={newInvite.prefill?.tiktok || ""}
                            onChange={(e) => setNewInvite(prev => ({ 
                              ...prev, 
                              prefill: { ...prev.prefill, tiktok: e.target.value }
                            }))}
                            placeholder="@handle"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Website</Label>
                          <Input
                            value={newInvite.prefill?.website || ""}
                            onChange={(e) => setNewInvite(prev => ({ 
                              ...prev, 
                              prefill: { ...prev.prefill, website: e.target.value }
                            }))}
                            placeholder="website.com"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Facebook</Label>
                          <Input
                            value={newInvite.prefill?.facebook || ""}
                            onChange={(e) => setNewInvite(prev => ({ 
                              ...prev, 
                              prefill: { ...prev.prefill, facebook: e.target.value }
                            }))}
                            placeholder="facebook.com/..."
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Booking Platforms */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold text-gray-700 uppercase">Booking Tools</Label>
                      <div className="flex flex-wrap gap-2">
                        {["Acuity", "Calendly", "Square", "Vagaro", "Fresha", "StyleSeat", "Booksy", "Mindbody"].map(platform => {
                          const isSelected = (newInvite.prefill?.booking_platforms || []).includes(platform);
                          return (
                            <button
                              key={platform}
                              type="button"
                              onClick={() => {
                                const current = newInvite.prefill?.booking_platforms || [];
                                const updated = isSelected 
                                  ? current.filter(p => p !== platform)
                                  : [...current, platform];
                                setNewInvite(prev => ({
                                  ...prev,
                                  prefill: { ...prev.prefill, booking_platforms: updated }
                                }));
                              }}
                              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                isSelected 
                                  ? "bg-purple-600 text-white border-purple-600" 
                                  : "bg-white text-gray-600 border-gray-300 hover:border-purple-400"
                              }`}
                            >
                              {platform}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Payment Methods */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold text-gray-700 uppercase">Payment Methods</Label>
                      <div className="flex flex-wrap gap-2">
                        {["Venmo", "CashApp", "Zelle", "Square", "Stripe", "PayPal", "Cash", "Card"].map(method => {
                          const isSelected = (newInvite.prefill?.payment_methods || []).includes(method);
                          return (
                            <button
                              key={method}
                              type="button"
                              onClick={() => {
                                const current = newInvite.prefill?.payment_methods || [];
                                const updated = isSelected 
                                  ? current.filter(p => p !== method)
                                  : [...current, method];
                                setNewInvite(prev => ({
                                  ...prev,
                                  prefill: { ...prev.prefill, payment_methods: updated }
                                }));
                              }}
                              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                isSelected 
                                  ? "bg-green-600 text-white border-green-600" 
                                  : "bg-white text-gray-600 border-gray-300 hover:border-green-400"
                              }`}
                            >
                              {method}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Business Info */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Location (City)</Label>
                        <Input
                          value={newInvite.prefill?.location || ""}
                          onChange={(e) => setNewInvite(prev => ({ 
                            ...prev, 
                            prefill: { ...prev.prefill, location: e.target.value }
                          }))}
                          placeholder="Los Angeles, CA"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Years Experience</Label>
                        <Input
                          value={newInvite.prefill?.years_experience || ""}
                          onChange={(e) => setNewInvite(prev => ({ 
                            ...prev, 
                            prefill: { ...prev.prefill, years_experience: e.target.value }
                          }))}
                          placeholder="5"
                          className="text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Services */}
                    <div>
                      <Label className="text-xs">Services Offered</Label>
                      <Textarea
                        value={newInvite.prefill?.services_offered || ""}
                        onChange={(e) => setNewInvite(prev => ({ 
                          ...prev, 
                          prefill: { ...prev.prefill, services_offered: e.target.value }
                        }))}
                        placeholder="Bridal makeup, Editorial looks, Special effects..."
                        className="text-sm"
                        rows={2}
                      />
                    </div>
                    
                    {/* Admin Notes */}
                    <div>
                      <Label className="text-xs">Admin Notes (not shown to provider)</Label>
                      <Textarea
                        value={newInvite.prefill?.admin_notes || ""}
                        onChange={(e) => setNewInvite(prev => ({ 
                          ...prev, 
                          prefill: { ...prev.prefill, admin_notes: e.target.value }
                        }))}
                        placeholder="Internal notes about this provider..."
                        className="text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {/* Confirmation Page Customization Toggle */}
                <button
                  type="button"
                  onClick={() => setShowConfirmationEditor(!showConfirmationEditor)}
                  className="w-full flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Customize Confirmation Page</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-green-600 transition-transform ${showConfirmationEditor ? "rotate-180" : ""}`} />
                </button>

                {/* Per-Invite Confirmation Editor */}
                {showConfirmationEditor && (
                  <InlineConfirmationEditor
                    settings={newInvite.confirmation_settings}
                    onUpdate={(updated) => setNewInvite(prev => ({ 
                      ...prev, 
                      confirmation_settings: updated 
                    }))}
                    categoryPreset={selectedSubCategory?.name?.toLowerCase() || expandedCategory}
                    showGatedContent={true}
                    showVirtualEvent={true}
                    compact={false}
                  />
                )}
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={resetInviteForm} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateInvite} 
                  disabled={creatingInvite || (!newInvite.email && !selectedUser)}
                  className="w-full sm:w-auto"
                >
                  {creatingInvite ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Create Invite
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Invite Dialog */}
      <Dialog open={!!editingInvite} onOpenChange={(open) => !open && setEditingInvite(null)}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>Edit Invite</DialogTitle>
            <DialogDescription>
              Update invite details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editInviteData.name}
                onChange={(e) => setEditInviteData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Guest name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editInviteData.email}
                onChange={(e) => setEditInviteData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <Label>Company / Business Name</Label>
              <Input
                value={editInviteData.company_name}
                onChange={(e) => setEditInviteData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Company name"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditingInvite(null)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveInvite} 
              disabled={savingInvite}
              className="w-full sm:w-auto"
            >
              {savingInvite ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Invite Confirmation Dialog */}
      <Dialog open={!!deletingInvite} onOpenChange={(open) => !open && setDeletingInvite(null)}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Invite
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this invite for <strong>{deletingInvite?.name || deletingInvite?.email}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeletingInvite(null)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteInvite}
              variant="destructive"
              className="w-full sm:w-auto"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Portal Dialog */}
      <Dialog open={!!editingPortal} onOpenChange={(open) => !open && setEditingPortal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Portal</DialogTitle>
            <DialogDescription>
              Update portal name and description
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="portal-name">Portal Name</Label>
              <Input
                id="portal-name"
                value={editPortalData.name}
                onChange={(e) => setEditPortalData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Hair, Makeup, Wellness"
              />
            </div>
            <div>
              <Label htmlFor="portal-description">Description</Label>
              <Textarea
                id="portal-description"
                value={editPortalData.description}
                onChange={(e) => setEditPortalData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this portal"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPortal(null)}>
              Cancel
            </Button>
            <Button onClick={handleSavePortal} disabled={savingPortal}>
              {savingPortal ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Portal Confirmation Dialog */}
      <Dialog open={!!deletingPortal} onOpenChange={(open) => !open && setDeletingPortal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Portal
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingPortal?.name}</strong>?
              This will also delete all associated invites and members.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeletingPortal(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeletePortal}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Portal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Journey View Dialog - Shows all stage links for a member or invite */}
      <Dialog open={showJourneyDialog} onOpenChange={setShowJourneyDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map className="w-5 h-5 text-purple-500" />
              Journey Stages
            </DialogTitle>
            <DialogDescription>
              {journeyPerson?.type === 'member' ? (
                <>View all journey stages for <span className="font-medium">{journeyPerson?.user_name || journeyPerson?.user_email}</span></>
              ) : (
                <>View all journey stages for <span className="font-medium">{journeyPerson?.name || journeyPerson?.email}</span></>
              )}
            </DialogDescription>
          </DialogHeader>

          {journeyPerson && (
            <div className="space-y-2 py-2">
              <p className="text-xs text-gray-600 font-medium mb-2">Click to preview each stage:</p>
              
              {/* Stage 1: Invite Page */}
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold shrink-0">1</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Invite Page</p>
                  <p className="text-xs text-gray-400">Initial invitation</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => {
                    const token = journeyPerson.magic_token || journeyPerson.invite_token;
                    if (token) window.open(`${window.location.origin}/pilot/invite/${token}`, '_blank');
                    else toast.error("No invite token found");
                  }}>
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => {
                    const token = journeyPerson.magic_token || journeyPerson.invite_token;
                    if (token) {
                      navigator.clipboard.writeText(`${window.location.origin}/pilot/invite/${token}`);
                      toast.success("Copied!");
                    }
                  }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              
              {/* Stage 2: Welcome Page */}
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                <div className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-bold shrink-0">2</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Welcome Page</p>
                  <p className="text-xs text-gray-400">After accepting invite</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                  const token = journeyPerson.magic_token || journeyPerson.invite_token;
                  if (token) window.open(`${window.location.origin}/pilot/welcome/${token}`, '_blank');
                  else toast.error("No token found");
                }}>
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </div>
              
              {/* Stage 3: NDA Page */}
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold shrink-0">3</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">NDA/Agreement</p>
                  <p className="text-xs text-gray-400">Sign partnership agreement</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                  const token = journeyPerson.magic_token || journeyPerson.invite_token;
                  if (token) window.open(`${window.location.origin}/pilot/nda/${token}`, '_blank');
                  else toast.error("No token found");
                }}>
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </div>
              
              {/* Stage 4: Intake Form */}
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold shrink-0">4</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Intake Form</p>
                  <p className="text-xs text-gray-400">Fill out profile details</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                  const token = journeyPerson.magic_token || journeyPerson.invite_token;
                  if (token) window.open(`${window.location.origin}/pilot/intake/${token}`, '_blank');
                  else toast.error("No token found");
                }}>
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </div>
              
              {/* Stage 5: Portal Dashboard */}
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border">
                <div className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-bold shrink-0">5</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Portal Dashboard</p>
                  <p className="text-xs text-gray-400">Final destination</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                  const token = journeyPerson.magic_token || journeyPerson.invite_token;
                  if (token) window.open(`${window.location.origin}/pilot/portal/${token}`, '_blank');
                  else window.open(`${window.location.origin}/front-row`, '_blank');
                }}>
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </div>
              
              {/* Stage 6: Category Demo Page */}
              <div className="flex items-center gap-2 p-2 bg-purple-50 rounded border border-purple-200">
                <div className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-bold shrink-0">★</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-purple-700">Category Demo Page</p>
                  <p className="text-xs text-purple-500">Public demo for this category</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 shrink-0 border-purple-200" onClick={() => {
                  window.open(`${window.location.origin}/${expandedCategory}`, '_blank');
                }}>
                  <ExternalLink className="w-3 h-3 text-purple-600" />
                </Button>
              </div>

              {/* Current Status */}
              <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                <p className="text-xs font-medium text-gray-600 mb-1">Current Status</p>
                <div className="flex items-center gap-2">
                  {journeyPerson.type === 'member' ? (
                    <Badge className="bg-emerald-500">Active Member</Badge>
                  ) : (
                    <Badge variant="secondary">Pending Invite</Badge>
                  )}
                  {journeyPerson.nda_signed && <Badge variant="outline" className="text-xs">NDA Signed</Badge>}
                  {journeyPerson.intake_completed && <Badge variant="outline" className="text-xs">Intake Done</Badge>}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJourneyDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ============================================================================
// PORTAL SETTINGS EDITOR COMPONENT
// ============================================================================
function PortalSettingsEditor({ subCategory, categoryType, onSave }) {
  const [settings, setSettings] = useState(subCategory?.settings || {});
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("branding");

  // Initialize with existing settings
  useEffect(() => {
    if (subCategory?.settings) {
      setSettings(subCategory.settings);
    }
  }, [subCategory]);

  const updateSetting = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const parts = path.split(".");
      let current = newSettings;
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = current[parts[i]] ? { ...current[parts[i]] } : {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return newSettings;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(settings);
    setSaving(false);
  };

  const sections = [
    { id: "branding", label: "Invite Design", icon: Sparkles },
    { id: "content", label: "Template Content", icon: FileText },
    { id: "welcome", label: "Welcome Page", icon: User },
    { id: "invite", label: "Invite Page", icon: Mail },
    { id: "nda", label: "NDA", icon: Eye },
    { id: "toggles", label: "Toggles", icon: Settings }
  ];

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        {sections.map(section => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                activeSection === section.id
                  ? "bg-purple-100 text-purple-700"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
            >
              <Icon className="w-4 h-4" />
              {section.label}
            </button>
          );
        })}
      </div>

      {/* Branding Section */}
      {activeSection === "branding" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium mb-4">Branding</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <ImageUploadField
                  label="Logo"
                  value={settings.logo_url}
                  onChange={(url) => updateSetting("logo_url", url)}
                  subCategoryId={subCategoryId}
                  imageType="logo"
                  previewSize="h-24"
                />
              </div>
              <div>
                <ImageUploadField
                  label="Cover Image"
                  value={settings.cover_url}
                  onChange={(url) => updateSetting("cover_url", url)}
                  subCategoryId={subCategoryId}
                  imageType="cover"
                />
              </div>
              <div>
                <Label>Brand Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={settings.brand_color || "#8B5CF6"}
                    onChange={(e) => updateSetting("brand_color", e.target.value)}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={settings.brand_color || "#8B5CF6"}
                    onChange={(e) => updateSetting("brand_color", e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label>Accent Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={settings.accent_color || "#EC4899"}
                    onChange={(e) => updateSetting("accent_color", e.target.value)}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={settings.accent_color || "#EC4899"}
                    onChange={(e) => updateSetting("accent_color", e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TEMPLATE CONTENT SECTION - Editable text & images for all pilot pages */}
      {activeSection === "content" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-lg">Template Content</h3>
              <p className="text-sm text-gray-500">Edit all text and images that appear in this pilot</p>
            </div>
            <Badge variant="outline">Super Admin Only</Badge>
          </div>

          {/* Sales Page Content */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-purple-600" />
              Sales/Landing Page
            </h4>
            <div className="space-y-4">
              <div>
                <Label>Headline</Label>
                <Input
                  value={settings.content?.sales_headline || ""}
                  onChange={(e) => updateSetting("content.sales_headline", e.target.value)}
                  placeholder="Transform Your Beauty Business"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Subheadline</Label>
                <Input
                  value={settings.content?.sales_subheadline || ""}
                  onChange={(e) => updateSetting("content.sales_subheadline", e.target.value)}
                  placeholder="Join the pilot program and get lifetime access"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Hero Description</Label>
                <Textarea
                  value={settings.content?.sales_description || ""}
                  onChange={(e) => updateSetting("content.sales_description", e.target.value)}
                  placeholder="Describe the value proposition..."
                  rows={4}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Hero Image</Label>
                <ImageUploadField
                  label=""
                  value={settings.content?.sales_hero_image}
                  onChange={(url) => updateSetting("content.sales_hero_image", url)}
                  subCategoryId={subCategoryId}
                  imageType="sales_hero"
                  placeholder="Upload Hero Image"
                  previewSize="h-32 w-48"
                />
              </div>
            </div>
          </div>

          {/* Benefits/Features Section */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-600" />
              Benefits & Features
            </h4>
            <div className="space-y-4">
              {[1, 2, 3].map(num => (
                <div key={num} className="p-3 bg-white rounded border">
                  <Label className="text-xs text-gray-500">Benefit #{num}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    <Input
                      value={settings.content?.[`benefit_${num}_title`] || ""}
                      onChange={(e) => updateSetting(`content.benefit_${num}_title`, e.target.value)}
                      placeholder={`Benefit ${num} Title`}
                    />
                    <Input
                      value={settings.content?.[`benefit_${num}_desc`] || ""}
                      onChange={(e) => updateSetting(`content.benefit_${num}_desc`, e.target.value)}
                      placeholder={`Short description...`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonials */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600" />
              Testimonials
            </h4>
            <div className="space-y-4">
              {[1, 2].map(num => (
                <div key={num} className="p-3 bg-white rounded border">
                  <Label className="text-xs text-gray-500">Testimonial #{num}</Label>
                  <div className="space-y-2 mt-2">
                    <Textarea
                      value={settings.content?.[`testimonial_${num}_quote`] || ""}
                      onChange={(e) => updateSetting(`content.testimonial_${num}_quote`, e.target.value)}
                      placeholder={`"Quote from satisfied client..."`}
                      rows={2}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={settings.content?.[`testimonial_${num}_name`] || ""}
                        onChange={(e) => updateSetting(`content.testimonial_${num}_name`, e.target.value)}
                        placeholder="Name"
                      />
                      <Input
                        value={settings.content?.[`testimonial_${num}_title`] || ""}
                        onChange={(e) => updateSetting(`content.testimonial_${num}_title`, e.target.value)}
                        placeholder="Title / Business"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Email Templates */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4 text-orange-600" />
              Email Templates
            </h4>
            <div className="space-y-4">
              <div>
                <Label>Invite Email Subject</Label>
                <Input
                  value={settings.content?.invite_email_subject || ""}
                  onChange={(e) => updateSetting("content.invite_email_subject", e.target.value)}
                  placeholder="You're invited to join the Phygital pilot!"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Invite Email Body</Label>
                <Textarea
                  value={settings.content?.invite_email_body || ""}
                  onChange={(e) => updateSetting("content.invite_email_body", e.target.value)}
                  placeholder="Hi {name},\n\nYou've been selected..."
                  rows={5}
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">Use {`{name}`}, {`{email}`}, {`{link}`} as placeholders</p>
              </div>
            </div>
          </div>

          {/* CTA Text */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-purple-600" />
              Call-to-Action Text
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Primary CTA Button</Label>
                <Input
                  value={settings.content?.cta_primary || ""}
                  onChange={(e) => updateSetting("content.cta_primary", e.target.value)}
                  placeholder="Join the Pilot"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Secondary CTA Button</Label>
                <Input
                  value={settings.content?.cta_secondary || ""}
                  onChange={(e) => updateSetting("content.cta_secondary", e.target.value)}
                  placeholder="Learn More"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Complete Intake CTA</Label>
                <Input
                  value={settings.content?.cta_intake || ""}
                  onChange={(e) => updateSetting("content.cta_intake", e.target.value)}
                  placeholder="Complete Your Profile"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>View Dashboard CTA</Label>
                <Input
                  value={settings.content?.cta_dashboard || ""}
                  onChange={(e) => updateSetting("content.cta_dashboard", e.target.value)}
                  placeholder="Go to Dashboard"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Page Section */}
      {activeSection === "welcome" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium mb-4">Welcome Page Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="welcome_enabled"
                  checked={settings.welcome_page?.enabled ?? true}
                  onChange={(e) => updateSetting("welcome_page.enabled", e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="welcome_enabled">Enable Welcome Page</Label>
              </div>
              <div>
                <Label>Welcome Title</Label>
                <Input
                  value={settings.welcome_page?.title || ""}
                  onChange={(e) => updateSetting("welcome_page.title", e.target.value)}
                  placeholder="Welcome to Phygital"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input
                  value={settings.welcome_page?.subtitle || ""}
                  onChange={(e) => updateSetting("welcome_page.subtitle", e.target.value)}
                  placeholder="You're in! Here's what's next..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={settings.welcome_page?.description || ""}
                  onChange={(e) => updateSetting("welcome_page.description", e.target.value)}
                  placeholder="Tell them what to expect..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="show_steps"
                  checked={settings.welcome_page?.show_steps ?? true}
                  onChange={(e) => updateSetting("welcome_page.show_steps", e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="show_steps">Show Onboarding Steps</Label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Page Section */}
    </div>
  );
}


// ============================================================================
// PORTAL ADVOCATE SETTINGS - Manage advocate configuration for any portal
// ============================================================================
function PortalAdvocateSettings({ subCategory, categoryType, subCategoryId, members }) {
  const [advocates, setAdvocates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advocateEnabled, setAdvocateEnabled] = useState(subCategory?.advocate_enabled !== false);

  useEffect(() => {
    fetchAdvocates();
  }, []);

  const fetchAdvocates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/brand-advocates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdvocates(data.advocates || []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const toggleAdvocate = async () => {
    const newVal = !advocateEnabled;
    setAdvocateEnabled(newVal);
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/admin/portal-categories/${categoryType}/sub-categories/${subCategoryId}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ settings: { advocate_enabled: newVal } })
      });
      toast.success(newVal ? "Advocate tab enabled for this portal" : "Advocate tab disabled");
    } catch { toast.error("Failed to update"); }
  };

  // Match members who are also advocates
  const memberEmails = new Set((members || []).map(m => m.email?.toLowerCase()));
  const matchedAdvocates = advocates.filter(a => memberEmails.has(a.email?.toLowerCase()));
  const availableAdvocates = advocates.filter(a => !memberEmails.has(a.email?.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="portal-advocate-settings">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
        <div>
          <h3 className="font-medium text-amber-900">Advocate Dashboard Access</h3>
          <p className="text-sm text-amber-700 mt-1">
            When enabled, members of this portal can access the Advocate Dashboard to track referrals and commission earnings.
          </p>
        </div>
        <Switch
          checked={advocateEnabled}
          onCheckedChange={toggleAdvocate}
          data-testid="advocate-enable-toggle"
        />
      </div>

      {advocateEnabled && (
        <>
          {/* Matched Advocates */}
          <div>
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-600" />
              Portal Members Who Are Advocates ({matchedAdvocates.length})
            </h4>
            {matchedAdvocates.length === 0 ? (
              <p className="text-sm text-gray-400 p-4 bg-gray-50 rounded-lg text-center">
                No members of this portal are currently advocates. Add them as advocates in the Super Admin panel.
              </p>
            ) : (
              <div className="space-y-2">
                {matchedAdvocates.map(adv => (
                  <div key={adv.advocate_id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{adv.first_name} {adv.last_name}</p>
                      <p className="text-xs text-gray-500">{adv.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-amber-100 text-amber-800 text-xs">{adv.commission_rate || 5}% commission</Badge>
                      <Badge variant="outline" className="text-xs">{adv.referrals_count || 0} referrals</Badge>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => window.open('/advocate', '_blank')}>
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="p-4 bg-gray-50 rounded-lg border">
            <h4 className="font-medium text-sm mb-3">Quick Links</h4>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" size="sm" className="justify-start gap-2" onClick={() => window.open('/advocate', '_blank')}>
                <ExternalLink className="w-3 h-3" /> Advocate Portal Preview
              </Button>
              <Button variant="outline" size="sm" className="justify-start gap-2" onClick={() => window.open('/admin/super', '_blank')}>
                <Settings className="w-3 h-3" /> Manage All Advocates
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              The Advocate Dashboard (/advocate) is accessible to all authenticated users with advocate status. 
              Commission rates and referral tracking are managed from Super Admin.
            </p>
          </div>
        </>
      )}
    </div>
  );
}


// ============================================================================
// PORTAL STATUS OVERVIEW - Shows stage of each invite in the pilot flow
// ============================================================================
function PortalStatusOverview({ members, pendingInvites, subCategoryName, onDeleteParticipant }) {
  // Define the stages
  const STAGES = [
    { id: "invited", label: "Invited", icon: Mail, color: "bg-blue-500" },
    { id: "accepted", label: "Accepted", icon: CheckCircle2, color: "bg-green-500" },
    { id: "nda_signed", label: "NDA Signed", icon: Eye, color: "bg-purple-500" },
    { id: "intake_complete", label: "Intake Done", icon: ClipboardList, color: "bg-orange-500" },
    { id: "active", label: "Active", icon: Sparkles, color: "bg-emerald-500" }
  ];

  const getStageIndex = (person) => {
    // Check for active/complete status first
    if (person.status === "active" || person.status === "complete") return 4;
    // Check intake completed status
    if (person.intake_completed || person.status === "intake_complete" || person.status === "intake_completed") return 3;
    // Check NDA signed status
    if (person.nda_signed || person.status === "nda_signed" || person.status === "intake") return 2;
    // Check accepted/pending_nda status
    if (person.status === "accepted" || person.status === "pending_nda" || person.status === "invite_accepted") return 1;
    // Default to invited/pending
    return 0;
  };

  // Combine all people
  const allPeople = [
    ...pendingInvites.map(inv => ({ ...inv, type: "invite" })),
    ...members.map(m => ({ ...m, type: "member" }))
  ];

  // Count by stage
  const stageCounts = STAGES.map((stage, idx) => ({
    ...stage,
    count: allPeople.filter(p => getStageIndex(p) === idx).length
  }));

  const handleDeleteParticipant = async (id, type) => {
    const typeLabel = type === "member" ? "member" : "invite";
    if (!window.confirm(`Are you sure you want to remove this ${typeLabel}?`)) return;
    if (onDeleteParticipant) {
      await onDeleteParticipant(id, type);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stage Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stageCounts.map((stage) => {
          const Icon = stage.icon;
          return (
            <div 
              key={stage.id}
              className="p-4 border rounded-lg text-center"
            >
              <div className={`w-10 h-10 ${stage.color} rounded-full flex items-center justify-center mx-auto mb-2`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-bold">{stage.count}</p>
              <p className="text-xs text-gray-500">{stage.label}</p>
            </div>
          );
        })}
      </div>

      {/* Detailed List */}
      <div className="space-y-3">
        <h4 className="font-medium text-gray-700">All Participants</h4>
        {allPeople.length === 0 ? (
          <div className="text-center py-8 border rounded-lg">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600">No participants yet</p>
            <p className="text-sm text-gray-500">Create invites to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {allPeople.map((person, idx) => {
              const stageIdx = getStageIndex(person);
              const currentStage = STAGES[stageIdx];
              const Icon = currentStage.icon;
              // Get display name: prioritize user_name (for members), name, recipient_name, then email prefix
              const displayName = person.user_name || person.name || person.recipient_name || person.company_name || 
                ((person.user_email || person.email) ? (person.user_email || person.email).split('@')[0] : 'Unknown');
              const displayEmail = person.user_email || person.email || '';
              
              return (
                <div 
                  key={person.invite_id || person.member_id || idx}
                  className="p-3 border rounded-lg flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 ${currentStage.color} rounded-full flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{displayName}</p>
                      {displayEmail && <p className="text-xs text-gray-500">{displayEmail}</p>}
                    </div>
                  </div>
                  
                  {/* Progress Bar and Actions */}
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {STAGES.map((s, i) => (
                        <div 
                          key={s.id}
                          className={`w-6 h-2 rounded-full ${i <= stageIdx ? currentStage.color : "bg-gray-200"}`}
                        />
                      ))}
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {currentStage.label}
                    </Badge>
                    {/* Delete button - show for both invites and members */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteParticipant(person.invite_id || person.member_id, person.type)}
                      className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PORTAL SETTINGS WITH PREVIEW - Settings editor + live preview of each step
// ============================================================================
function PortalSettingsWithPreview({ subCategory, categoryType, subCategoryId, onSave }) {
  const [activeSection, setActiveSection] = useState("preview");
  const [settings, setSettings] = useState(subCategory?.settings || {});
  const [previewStep, setPreviewStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [previewMode, setPreviewMode] = useState("desktop"); // desktop, tablet, mobile
  const [isLoading, setIsLoading] = useState(false);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(settings);
    setSaving(false);
  };

  // Get a test invite token for preview (first pending invite or demo token)
  const getPreviewToken = () => {
    // Use a demo token format that the system can recognize as preview mode
    return `preview_${subCategoryId}_${categoryType}`;
  };

  // Preview steps with actual URLs
  const PREVIEW_STEPS = [
    { 
      id: "invite", 
      label: "Invite Page", 
      desc: "What they see when clicking the invite link",
      getUrl: () => `/portal/invite/${getPreviewToken()}?preview=true`
    },
    { 
      id: "welcome", 
      label: "Welcome Page", 
      desc: "After accepting the invite",
      getUrl: () => `/pilot/welcome/${getPreviewToken()}?preview=true`
    },
    { 
      id: "nda", 
      label: "NDA Page", 
      desc: "Digital signature experience",
      getUrl: () => `/pilot/nda/${getPreviewToken()}?preview=true`
    },
    { 
      id: "intake", 
      label: "Intake Flow", 
      desc: "5-section questionnaire",
      getUrl: () => `/pilot/intake/${getPreviewToken()}?preview=true`
    },
    { 
      id: "dashboard", 
      label: "Portal Dashboard", 
      desc: "Their final pilot portal",
      getUrl: () => `/pilot/portal/${getPreviewToken()}?preview=true`
    }
  ];

  const sections = [
    { id: "preview", label: "Live Preview", icon: Eye },
    { id: "branding", label: "Envelope & Cover", icon: ImageIcon },
    { id: "welcome", label: "Welcome Page", icon: User },
    { id: "invite", label: "Invite Page", icon: Mail },
    { id: "nda", label: "NDA", icon: FileText },
    { id: "defaults", label: "Pilot Defaults", icon: Settings },
    { id: "host_dashboard", label: "Host Dashboard", icon: LayoutDashboard },
    { id: "confirmation", label: "Confirmation & Gated Content", icon: CheckCircle2 },
    { id: "toggles", label: "Toggles", icon: Settings }
  ];

  // Refresh iframe when settings change
  const refreshPreview = () => {
    setIsLoading(true);
    setIframeKey(prev => prev + 1);
  };

  // Preview width based on mode
  const previewWidths = {
    desktop: "100%",
    tablet: "768px",
    mobile: "375px"
  };

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        {sections.map(section => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                activeSection === section.id
                  ? "bg-purple-100 text-purple-700"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
            >
              <Icon className="w-4 h-4" />
              {section.label}
            </button>
          );
        })}
      </div>

      {/* Live Preview Section - Now with iframe */}
      {activeSection === "preview" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-medium">Live Portal Preview</h3>
              <p className="text-sm text-gray-500">Interactive preview of actual pages</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Device Mode Selector */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {[
                  { id: "desktop", icon: Monitor, label: "Desktop" },
                  { id: "tablet", icon: Sparkles, label: "Tablet" },
                  { id: "mobile", icon: User, label: "Mobile" }
                ].map(mode => (
                  <button
                    key={mode.id}
                    onClick={() => setPreviewMode(mode.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      previewMode === mode.id
                        ? "bg-white text-purple-700 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                    title={mode.label}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={refreshPreview}>
                <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Step Selector */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {PREVIEW_STEPS.map((step, idx) => (
              <button
                key={step.id}
                onClick={() => { setPreviewStep(idx); setIsLoading(true); }}
                className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
                  previewStep === idx
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                  {idx + 1}
                </span>
                {step.label}
              </button>
            ))}
          </div>

          {/* Preview Frame with actual iframe */}
          <div className="border rounded-lg overflow-hidden bg-gray-100">
            <div className="bg-gray-800 px-4 py-2 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="flex-1 text-center">
                <span className="text-xs text-gray-400 font-mono">
                  {PREVIEW_STEPS[previewStep].getUrl()}
                </span>
              </div>
              <button
                onClick={() => window.open(PREVIEW_STEPS[previewStep].getUrl(), '_blank')}
                className="text-gray-400 hover:text-white transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
            
            {/* Iframe Container with responsive width */}
            <div 
              className="flex justify-center bg-gray-200 p-4 overflow-auto"
              style={{ minHeight: "500px" }}
            >
              <div 
                className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300"
                style={{ 
                  width: previewWidths[previewMode],
                  maxWidth: "100%",
                  height: previewMode === "mobile" ? "667px" : "500px"
                }}
              >
                {isLoading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                    <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                )}
                <iframe
                  key={iframeKey}
                  src={PREVIEW_STEPS[previewStep].getUrl()}
                  className="w-full h-full border-0"
                  onLoad={() => setIsLoading(false)}
                  title={`Preview: ${PREVIEW_STEPS[previewStep].label}`}
                  sandbox="allow-same-origin allow-scripts allow-forms"
                />
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {PREVIEW_STEPS[previewStep].desc}
            </p>
            <Badge variant="outline" className="text-xs">
              Preview Mode - Changes reflect in real-time
            </Badge>
          </div>
        </div>
      )}

      {/* ENVELOPE/COVER SECTION - Matching Host Dashboard Style */}
      {activeSection === "branding" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-lg">Invite Design</h3>
              <p className="text-sm text-gray-500">Customize the invite envelope appearance</p>
            </div>
          </div>

          {/* Logo Upload - Main visual element */}
          <div className="p-4 border rounded-xl bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-medium text-sm">Logo</h4>
                <p className="text-xs text-gray-500">Appears on the envelope sticker</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <ImageUploadField
                label=""
                value={settings.logo_url}
                onChange={(url) => updateSetting("logo_url", url)}
                subCategoryId={subCategoryId}
                imageType="logo"
                placeholder="Upload Logo"
                previewSize="h-32 w-32"
              />
              <div className="flex-1">
                <p className="text-xs text-gray-600 mb-2">
                  This logo appears as the envelope sticker. Square images work best.
                </p>
                <p className="text-xs text-gray-400">Recommended: 512x512 PNG with transparent background</p>
              </div>
            </div>
          </div>

          {/* Background Upload */}
          <div className="p-4 border rounded-xl bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-medium text-sm">Background</h4>
                <p className="text-xs text-gray-500">The backdrop behind the envelope</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <ImageUploadField
                label=""
                value={settings.envelope_background}
                onChange={(url) => updateSetting("envelope_background", url)}
                subCategoryId={subCategoryId}
                imageType="envelope_bg"
                placeholder="Upload Background"
                previewSize="h-24 w-40"
              />
              <div className="flex-1">
                <p className="text-xs text-gray-600 mb-2">
                  This image appears behind the envelope. Use a high-quality image that matches your brand.
                </p>
                <p className="text-xs text-gray-400">Recommended: 1920x1080 or larger</p>
              </div>
            </div>
          </div>

          {/* Envelope Preview */}
          <div className="p-4 border rounded-xl bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-medium text-sm">Preview</h4>
                <p className="text-xs text-gray-500">How your invite will look</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Animation</span>
                <Switch 
                  checked={settings.envelope_animation !== "none"}
                  onCheckedChange={(checked) => updateSetting("envelope_animation", checked ? "slide" : "none")}
                />
              </div>
            </div>
            
            {/* Envelope Preview - Matching HTHF style */}
            <div 
              className="relative mx-auto w-72 h-52 rounded-xl overflow-hidden flex items-center justify-center"
              style={settings.envelope_background ? {
                backgroundImage: `url(${settings.envelope_background})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              } : {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              {/* Envelope */}
              <div className="relative">
                {/* White envelope card */}
                <div className="w-44 h-32 bg-white rounded-lg shadow-lg relative overflow-hidden">
                  {/* Envelope flap */}
                  <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-gray-100 to-white" 
                       style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)' }} />
                  {/* Inner card */}
                  <div className="absolute inset-3 top-5 bg-gray-50 rounded border border-gray-200" />
                </div>
                
                {/* Logo Sticker - Uses Phygital logo or uploaded logo */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center shadow-md overflow-hidden">
                    {settings.logo_url ? (
                      <img src={settings.logo_url} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white font-bold text-xs">PH</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* "You're Invited" text */}
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-white/90 text-[10px] tracking-[0.2em] uppercase drop-shadow-lg">YOU'RE INVITED</p>
              </div>
            </div>
            
            <p className="text-center text-[10px] text-gray-400 mt-3">
              Recipients see this before the welcome page
            </p>
          </div>
        </div>
      )}

      {/* WELCOME PAGE SECTION - Full Edit */}
      {activeSection === "welcome" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium text-lg">Welcome Page Settings</h3>
            <p className="text-sm text-gray-500">Configure the welcome experience after they accept the invite</p>
          </div>

          {/* Welcome Header */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Header Content</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Welcome Title</Label>
                <Input
                  value={settings.welcome_title || "Welcome to Phygital!"}
                  onChange={(e) => updateSetting("welcome_title", e.target.value)}
                  placeholder="Welcome to Phygital!"
                  className="mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Subtitle</Label>
                <Input
                  value={settings.welcome_subtitle || "You're one of our founding pilots"}
                  onChange={(e) => updateSetting("welcome_subtitle", e.target.value)}
                  placeholder="You're one of our founding pilots"
                  className="mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Welcome Description</Label>
                <Textarea
                  value={settings.welcome_description || ""}
                  onChange={(e) => updateSetting("welcome_description", e.target.value)}
                  placeholder="Add a detailed description or personalized message..."
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Steps Configuration */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Onboarding Steps Display</h4>
              <Switch
                checked={settings.show_steps !== false}
                onCheckedChange={(checked) => updateSetting("show_steps", checked)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Step 1 Label</Label>
                <Input
                  value={settings.step1_label || "Sign NDA"}
                  onChange={(e) => updateSetting("step1_label", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Step 2 Label</Label>
                <Input
                  value={settings.step2_label || "Complete Intake"}
                  onChange={(e) => updateSetting("step2_label", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Step 3 Label</Label>
                <Input
                  value={settings.step3_label || "Access Portal"}
                  onChange={(e) => updateSetting("step3_label", e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Benefits List */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Benefits / Value Props</h4>
            <div className="space-y-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={settings[`benefit_${i}`] || ""}
                    onChange={(e) => updateSetting(`benefit_${i}`, e.target.value)}
                    placeholder={`Benefit ${i} (e.g., "Early access to new features")`}
                    className="flex-1"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Custom HTML */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Custom HTML (Advanced)</h4>
            <Textarea
              value={settings.welcome_custom_html || ""}
              onChange={(e) => updateSetting("welcome_custom_html", e.target.value)}
              placeholder="<div>Custom HTML content...</div>"
              rows={4}
              className="mt-1 font-mono text-sm"
            />
          </div>
        </div>
      )}

      {/* INVITE PAGE SECTION - Full Edit */}
      {activeSection === "invite" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium text-lg">Invite Page Settings</h3>
            <p className="text-sm text-gray-500">Configure the invite landing page experience</p>
          </div>

          {/* Invite Page Images */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Invite Page Images (4 slots)
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <ImageUploadField
                  label="1. Hero Background"
                  value={settings.invite_hero_image}
                  onChange={(url) => updateSetting("invite_hero_image", url)}
                  subCategoryId={subCategoryId}
                  imageType="invite_hero"
                  placeholder="Upload hero image"
                  previewSize="h-28"
                />
                <p className="text-xs text-gray-400 mt-1">Main background</p>
              </div>
              <div>
                <ImageUploadField
                  label="2. Preview Mockup"
                  value={settings.invite_preview_image}
                  onChange={(url) => updateSetting("invite_preview_image", url)}
                  subCategoryId={subCategoryId}
                  imageType="invite_preview"
                  placeholder="Upload preview"
                  previewSize="h-28"
                />
                <p className="text-xs text-gray-400 mt-1">Demo section mockup</p>
              </div>
              <div>
                <ImageUploadField
                  label="3. Demo Content"
                  value={settings.invite_demo_image}
                  onChange={(url) => updateSetting("invite_demo_image", url)}
                  subCategoryId={subCategoryId}
                  imageType="invite_demo"
                  placeholder="Upload demo content"
                  previewSize="h-28"
                />
                <p className="text-xs text-gray-400 mt-1">Portfolio/Work sample</p>
              </div>
              <div>
                <ImageUploadField
                  label="4. Category Badge"
                  value={settings.invite_category_image}
                  onChange={(url) => updateSetting("invite_category_image", url)}
                  subCategoryId={subCategoryId}
                  imageType="invite_badge"
                  placeholder="Upload badge"
                  previewSize="h-28"
                />
                <p className="text-xs text-gray-400 mt-1">Category icon</p>
              </div>
            </div>
          </div>

          {/* Hero Section */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Hero Section</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Headline (use {"{name}"} for personalization)</Label>
                <Input
                  value={settings.invite_headline || "Hi {name}, we'd love to work with you"}
                  onChange={(e) => updateSetting("invite_headline", e.target.value)}
                  placeholder="Hi {name}, we'd love to work with you"
                  className="mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Subheadline</Label>
                <Input
                  value={settings.invite_subheadline || ""}
                  onChange={(e) => updateSetting("invite_subheadline", e.target.value)}
                  placeholder="Join us in reimagining how you connect with clients"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>CTA Button Text</Label>
                <Input
                  value={settings.invite_cta_text || "Accept Invite"}
                  onChange={(e) => updateSetting("invite_cta_text", e.target.value)}
                  placeholder="Accept Invite"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>CTA Button Style</Label>
                <Select 
                  value={settings.invite_cta_style || "solid"} 
                  onValueChange={(v) => updateSetting("invite_cta_style", v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solid">Solid Color</SelectItem>
                    <SelectItem value="gradient">Gradient</SelectItem>
                    <SelectItem value="outline">Outline</SelectItem>
                    <SelectItem value="glow">Glow Effect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Demo Content */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Demo Content Section</h4>
              <Switch
                checked={settings.show_demo_content !== false}
                onCheckedChange={(checked) => updateSetting("show_demo_content", checked)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Demo Content Type</Label>
                <Select 
                  value={settings.demo_type || "default"} 
                  onValueChange={(v) => updateSetting("demo_type", v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default / Generic</SelectItem>
                    <SelectItem value="hair">Hair Services</SelectItem>
                    <SelectItem value="nail">Nail Services</SelectItem>
                    <SelectItem value="makeup">Makeup Services</SelectItem>
                    <SelectItem value="wellness">Wellness Services</SelectItem>
                    <SelectItem value="culinary">Culinary Services</SelectItem>
                    <SelectItem value="events">Events</SelectItem>
                    <SelectItem value="custom">Custom (use HTML below)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Demo Section Title</Label>
                <Input
                  value={settings.demo_section_title || "See what's possible"}
                  onChange={(e) => updateSetting("demo_section_title", e.target.value)}
                  placeholder="See what's possible"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Demo Feature Cards - Editable */}
          <div className="p-4 border rounded-lg bg-purple-50 border-purple-200">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-600" />
              Demo Feature Cards (3 sections)
            </h4>
            <p className="text-sm text-gray-500 mb-4">Customize the demo content shown on the invite page</p>
            
            {/* Card 1 - e.g., Skin Type */}
            <div className="p-4 bg-white rounded-lg border mb-4">
              <Label className="text-xs text-purple-600 font-semibold">Feature Card 1</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={settings.demo_card1_title || "Skin Type & Concerns"}
                    onChange={(e) => updateSetting("demo_card1_title", e.target.value)}
                    placeholder="Skin Type & Concerns"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Subtitle</Label>
                  <Input
                    value={settings.demo_card1_subtitle || "Know your client's skin before the appointment"}
                    onChange={(e) => updateSetting("demo_card1_subtitle", e.target.value)}
                    placeholder="Short description..."
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Options (comma-separated)</Label>
                  <Input
                    value={settings.demo_card1_options || "Oily, Dry, Combination, Sensitive"}
                    onChange={(e) => updateSetting("demo_card1_options", e.target.value)}
                    placeholder="Option 1, Option 2, Option 3, Option 4"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Card 2 - e.g., Look Inspiration with Images */}
            <div className="p-4 bg-white rounded-lg border mb-4">
              <Label className="text-xs text-purple-600 font-semibold">Feature Card 2 (with images)</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={settings.demo_card2_title || "Look Inspiration Boards"}
                    onChange={(e) => updateSetting("demo_card2_title", e.target.value)}
                    placeholder="Look Inspiration Boards"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Subtitle</Label>
                  <Input
                    value={settings.demo_card2_subtitle || "Clients share their vision with reference images"}
                    onChange={(e) => updateSetting("demo_card2_subtitle", e.target.value)}
                    placeholder="Short description..."
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs">Inspiration Images (4 slots)</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {[1, 2, 3, 4].map(num => (
                    <ImageUploadField
                      key={num}
                      label=""
                      value={settings[`demo_inspo_image_${num}`]}
                      onChange={(url) => updateSetting(`demo_inspo_image_${num}`, url)}
                      subCategoryId={subCategoryId}
                      imageType={`demo_inspo_${num}`}
                      placeholder={`Image ${num}`}
                      previewSize="h-20"
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Card 3 - Highlight Card */}
            <div className="p-4 bg-white rounded-lg border">
              <Label className="text-xs text-purple-600 font-semibold">Feature Card 3 (highlighted)</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={settings.demo_card3_title || "Product Preferences & Allergies"}
                    onChange={(e) => updateSetting("demo_card3_title", e.target.value)}
                    placeholder="Product Preferences & Allergies"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Subtitle</Label>
                  <Input
                    value={settings.demo_card3_subtitle || "Track favorite brands, known allergies, and skin reactions."}
                    onChange={(e) => updateSetting("demo_card3_subtitle", e.target.value)}
                    placeholder="Short description..."
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Collected Information</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: "collect_phone", label: "Phone Number" },
                { key: "collect_company", label: "Company Name" },
                { key: "collect_instagram", label: "Instagram" },
                { key: "collect_tiktok", label: "TikTok" },
                { key: "collect_linkedin", label: "LinkedIn" },
                { key: "collect_location", label: "Location/City" },
                { key: "collect_services", label: "Services Offered" },
                { key: "collect_website", label: "Website URL" }
              ].map(field => (
                <div key={field.key} className="flex items-center gap-2 p-2 border rounded">
                  <input
                    type="checkbox"
                    checked={settings[field.key] || false}
                    onChange={(e) => updateSetting(field.key, e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{field.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Content */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Custom Invite Content (Advanced)</h4>
            <Textarea
              value={settings.invite_custom_html || ""}
              onChange={(e) => updateSetting("invite_custom_html", e.target.value)}
              placeholder="Add custom HTML content to display on the invite page..."
              rows={4}
              className="mt-1 font-mono text-sm"
            />
          </div>
        </div>
      )}

      {/* NDA SECTION - Full Edit */}
      {activeSection === "nda" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium text-lg">NDA & Agreement Settings</h3>
            <p className="text-sm text-gray-500">Configure the non-disclosure agreement experience</p>
          </div>

          {/* NDA Toggles */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">NDA Requirements</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                <div>
                  <p className="font-medium">Require NDA Signature</p>
                  <p className="text-sm text-gray-500">Pilots must sign before proceeding</p>
                </div>
                <Switch
                  checked={settings.require_nda !== false}
                  onCheckedChange={(checked) => updateSetting("require_nda", checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                <div>
                  <p className="font-medium">Allow PDF Download</p>
                  <p className="text-sm text-gray-500">Let pilots download signed NDA</p>
                </div>
                <Switch
                  checked={settings.allow_nda_download !== false}
                  onCheckedChange={(checked) => updateSetting("allow_nda_download", checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                <div>
                  <p className="font-medium">Show Stealth Mode Notice</p>
                  <p className="text-sm text-gray-500">Display stealth mode disclaimer</p>
                </div>
                <Switch
                  checked={settings.show_stealth_notice !== false}
                  onCheckedChange={(checked) => updateSetting("show_stealth_notice", checked)}
                />
              </div>
            </div>
          </div>

          {/* NDA Content */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">NDA Content</h4>
            <div className="space-y-4">
              <div>
                <Label>NDA Title</Label>
                <Input
                  value={settings.nda_title || "Non-Disclosure Agreement"}
                  onChange={(e) => updateSetting("nda_title", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>NDA Template (leave blank for default)</Label>
                <Select 
                  value={settings.nda_template_id || "default"} 
                  onValueChange={(v) => updateSetting("nda_template_id", v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default NDA Template</SelectItem>
                    <SelectItem value="pilot">Pilot Partner NDA</SelectItem>
                    <SelectItem value="investor">Investor NDA</SelectItem>
                    <SelectItem value="custom">Custom (use text below)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Custom NDA Text (Markdown supported)</Label>
                <Textarea
                  value={settings.custom_nda || ""}
                  onChange={(e) => updateSetting("custom_nda", e.target.value)}
                  placeholder="Leave blank to use selected template..."
                  rows={8}
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label>Checkbox Agreement Text</Label>
                <Input
                  value={settings.nda_checkbox_text || "I agree to the terms above"}
                  onChange={(e) => updateSetting("nda_checkbox_text", e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PILOT DEFAULTS SECTION - Control which defaults categories apply to this pilot */}
      {activeSection === "defaults" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium text-lg">Pilot Default Categories</h3>
            <p className="text-sm text-gray-500">Select which default categories participants in this pilot can set preferences for</p>
          </div>

          {/* Universal Defaults */}
          <div className="p-4 border rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-500" />
              Universal Defaults
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { key: "dietary", label: "Dietary Restrictions", desc: "Allergies, restrictions, preferences" },
                { key: "accessibility", label: "Accessibility Needs", desc: "Mobility, sensory, communication needs" },
                { key: "interests", label: "Interests", desc: "Topics, activities, style preferences" },
                { key: "transportation", label: "Transportation", desc: "Ride needs, parking, transit preferences" }
              ].map(item => (
                <div 
                  key={item.key} 
                  className={`p-3 rounded-lg border ${
                    (settings.enabled_defaults || ["dietary", "accessibility", "interests"]).includes(item.key)
                      ? "bg-purple-50 border-purple-200"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                    <Switch
                      checked={(settings.enabled_defaults || ["dietary", "accessibility", "interests"]).includes(item.key)}
                      onCheckedChange={(checked) => {
                        const current = settings.enabled_defaults || ["dietary", "accessibility", "interests"];
                        const updated = checked 
                          ? [...current, item.key]
                          : current.filter(k => k !== item.key);
                        updateSetting("enabled_defaults", updated);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Beauty Defaults */}
          <div className="p-4 border rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-pink-500" />
              Beauty Defaults
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { key: "hair", label: "Hair Preferences", desc: "Color, texture, styling preferences" },
                { key: "nails", label: "Nail Preferences", desc: "Shape, length, colors, designs" },
                { key: "makeup", label: "Makeup Preferences", desc: "Coverage, style, skin type" },
                { key: "face", label: "Face/Skincare", desc: "Skin concerns, facials, treatments" },
                { key: "lashes", label: "Lash Preferences", desc: "Style, length, material" }
              ].map(item => (
                <div 
                  key={item.key} 
                  className={`p-3 rounded-lg border ${
                    (settings.enabled_defaults || []).includes(item.key)
                      ? "bg-pink-50 border-pink-200"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                    <Switch
                      checked={(settings.enabled_defaults || []).includes(item.key)}
                      onCheckedChange={(checked) => {
                        const current = settings.enabled_defaults || [];
                        const updated = checked 
                          ? [...current, item.key]
                          : current.filter(k => k !== item.key);
                        updateSetting("enabled_defaults", updated);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Lifestyle Defaults */}
          <div className="p-4 border rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4 text-sky-500" />
              Lifestyle Defaults
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { key: "travel", label: "Travel Preferences", desc: "Accommodations, activities, style" },
                { key: "wellness", label: "Wellness", desc: "Fitness, nutrition, mindfulness" },
                { key: "style", label: "Style/Fashion", desc: "Clothing size, style preferences" }
              ].map(item => (
                <div 
                  key={item.key} 
                  className={`p-3 rounded-lg border ${
                    (settings.enabled_defaults || []).includes(item.key)
                      ? "bg-sky-50 border-sky-200"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                    <Switch
                      checked={(settings.enabled_defaults || []).includes(item.key)}
                      onCheckedChange={(checked) => {
                        const current = settings.enabled_defaults || [];
                        const updated = checked 
                          ? [...current, item.key]
                          : current.filter(k => k !== item.key);
                        updateSetting("enabled_defaults", updated);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Default Settings"}
            </Button>
          </div>
        </div>
      )}

      {/* TOGGLES SECTION - Comprehensive */}
      {activeSection === "toggles" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium text-lg">Feature Toggles & Settings</h3>
            <p className="text-sm text-gray-500">Control all features and behaviors</p>
          </div>

          {/* Portal Experience */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Portal Experience</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { key: "show_updates_tab", label: "Show Updates Tab", desc: "Display platform updates" },
                { key: "show_experience_tab", label: "Show Experience Tab", desc: "Show their journey/roadmap" },
                { key: "show_chat_tab", label: "Show Chat Tab", desc: "Enable messaging with admin" },
                { key: "show_feedback_button", label: "Show Feedback Button", desc: "Quick feedback access" },
                { key: "show_resources", label: "Show Resources Section", desc: "Display helpful resources" },
                { key: "show_roadmap", label: "Show Roadmap", desc: "Display product roadmap" }
              ].map(toggle => (
                <div key={toggle.key} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                  <div>
                    <p className="font-medium text-sm">{toggle.label}</p>
                    <p className="text-xs text-gray-500">{toggle.desc}</p>
                  </div>
                  <Switch
                    checked={settings[toggle.key] !== false}
                    onCheckedChange={(checked) => updateSetting(toggle.key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Onboarding Flow */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Onboarding Flow</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { key: "require_intake", label: "Require Intake Form", desc: "Must complete questionnaire" },
                { key: "auto_approve", label: "Auto-Approve Pilots", desc: "Skip manual approval" },
                { key: "send_welcome_email", label: "Send Welcome Email", desc: "Email after acceptance" },
                { key: "send_reminder_emails", label: "Send Reminder Emails", desc: "Remind incomplete signups" },
                { key: "show_calendly", label: "Show Calendly Button", desc: "Offer scheduling call" },
                { key: "allow_skip_intake", label: "Allow Skip Intake", desc: "Let pilots skip questionnaire" }
              ].map(toggle => (
                <div key={toggle.key} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                  <div>
                    <p className="font-medium text-sm">{toggle.label}</p>
                    <p className="text-xs text-gray-500">{toggle.desc}</p>
                  </div>
                  <Switch
                    checked={settings[toggle.key] !== false}
                    onCheckedChange={(checked) => updateSetting(toggle.key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Event Features - NFC & Check-in */}
          <div className="p-4 border rounded-lg bg-purple-50 border-purple-200">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-600" />
              Event Features
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* NFC Kiosk - Luxe Only */}
              <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                <div>
                  <p className="font-medium text-sm flex items-center gap-2">
                    NFC Kiosk Check-in
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">LUXE</Badge>
                  </p>
                  <p className="text-xs text-gray-500">Enable NFC tap check-in at events</p>
                </div>
                <Switch
                  checked={settings.nfc_kiosk_enabled || false}
                  onCheckedChange={(checked) => updateSetting("nfc_kiosk_enabled", checked)}
                />
              </div>
              {/* Tap-In Music */}
              <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                <div>
                  <p className="font-medium text-sm">Tap-In Music Integration</p>
                  <p className="text-xs text-gray-500">Enable DJ insights & music preferences</p>
                </div>
                <Switch
                  checked={settings.tap_in_enabled || false}
                  onCheckedChange={(checked) => updateSetting("tap_in_enabled", checked)}
                />
              </div>
              {/* QR Code Check-in */}
              <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                <div>
                  <p className="font-medium text-sm">QR Code Check-in</p>
                  <p className="text-xs text-gray-500">Enable QR scan check-in</p>
                </div>
                <Switch
                  checked={settings.qr_checkin_enabled !== false}
                  onCheckedChange={(checked) => updateSetting("qr_checkin_enabled", checked)}
                />
              </div>
              {/* Gated Content */}
              <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                <div>
                  <p className="font-medium text-sm">Gated Content After Check-in</p>
                  <p className="text-xs text-gray-500">Reveal content only after check-in</p>
                </div>
                <Switch
                  checked={settings.gated_content_enabled || false}
                  onCheckedChange={(checked) => updateSetting("gated_content_enabled", checked)}
                />
              </div>
            </div>
          </div>

          {/* Branding Display */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Branding Display</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { key: "show_logo", label: "Show Logo", desc: "Display logo on pages" },
                { key: "show_company_name", label: "Show Company Name", desc: "Display Phygital branding" },
                { key: "show_powered_by", label: "Show 'Powered by Phygital'", desc: "Footer attribution" },
                { key: "use_custom_domain", label: "Use Custom Domain", desc: "Custom portal URL" }
              ].map(toggle => (
                <div key={toggle.key} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                  <div>
                    <p className="font-medium text-sm">{toggle.label}</p>
                    <p className="text-xs text-gray-500">{toggle.desc}</p>
                  </div>
                  <Switch
                    checked={settings[toggle.key] !== false}
                    onCheckedChange={(checked) => updateSetting(toggle.key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Contact & Support */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Contact & Support</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Support Email</Label>
                <Input
                  value={settings.contact_email || ""}
                  onChange={(e) => updateSetting("contact_email", e.target.value)}
                  placeholder="support@..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Support Phone</Label>
                <Input
                  value={settings.contact_phone || ""}
                  onChange={(e) => updateSetting("contact_phone", e.target.value)}
                  placeholder="+1..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Calendly URL</Label>
                <Input
                  value={settings.calendly_url || ""}
                  onChange={(e) => updateSetting("calendly_url", e.target.value)}
                  placeholder="https://calendly.com/..."
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Acuity Scheduling Integration - Simple OAuth */}
          <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  Acuity Scheduling
                </h4>
                <p className="text-sm text-gray-500">Connect to sync bookings & availability</p>
              </div>
              <Switch
                checked={settings.acuity_enabled || false}
                onCheckedChange={(checked) => updateSetting("acuity_enabled", checked)}
              />
            </div>
            
            {settings.acuity_enabled && (
              <div className="space-y-4 pt-4 border-t border-blue-200">
                {/* Connection Status */}
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-3">
                    {settings.acuity_connected ? (
                      <>
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <div>
                          <p className="text-sm font-medium text-green-700">Connected to Acuity</p>
                          <p className="text-xs text-gray-500">
                            {settings.acuity_business_name || "Business account linked"}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-3 h-3 rounded-full bg-gray-300" />
                        <div>
                          <p className="text-sm font-medium text-gray-600">Not connected</p>
                          <p className="text-xs text-gray-500">Click below to connect your Acuity account</p>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {settings.acuity_connected ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        updateSetting("acuity_connected", false);
                        updateSetting("acuity_access_token", null);
                        toast.success("Disconnected from Acuity");
                      }}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      Disconnect
                    </Button>
                  ) : (
                    <Button 
                      size="sm"
                      onClick={() => {
                        // For now, open a simple OAuth flow
                        // In production, this would redirect to Acuity OAuth
                        const acuityOAuthUrl = `https://acuityscheduling.com/oauth2/authorize?` +
                          `response_type=code&` +
                          `client_id=${encodeURIComponent(settings.acuity_client_id || 'YOUR_CLIENT_ID')}&` +
                          `redirect_uri=${encodeURIComponent(window.location.origin + '/api/acuity/callback')}&` +
                          `scope=api-v1&` +
                          `state=${subCategoryId}`;
                        
                        // Show instructions for now since OAuth needs backend setup
                        toast.info(
                          "Acuity OAuth setup needed. For now, ask the participant to share their Acuity credentials via secure message.",
                          { duration: 5000 }
                        );
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Connect with Acuity
                    </Button>
                  )}
                </div>
                
                {/* Simple Instructions for Participants */}
                {!settings.acuity_connected && (
                  <div className="p-3 bg-white rounded-lg border">
                    <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-blue-600" />
                      How to Connect (Simple Steps)
                    </h5>
                    <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside">
                      <li>
                        <strong>You'll click "Connect with Acuity"</strong> above
                      </li>
                      <li>
                        <strong>Log in to your Acuity account</strong> when prompted
                      </li>
                      <li>
                        <strong>Click "Allow"</strong> to let Phygital access your calendar
                      </li>
                      <li>
                        <strong>Done!</strong> Your bookings will sync automatically
                      </li>
                    </ol>
                    <p className="text-xs text-gray-400 mt-3 italic">
                      No technical knowledge needed - it's like logging into any app!
                    </p>
                  </div>
                )}
                
                {/* What Gets Synced */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Appointments", desc: "View & manage bookings" },
                    { label: "Availability", desc: "Your open time slots" },
                    { label: "Client Info", desc: "Names & contact details" },
                    { label: "Services", desc: "What you offer" }
                  ].map(item => (
                    <div key={item.label} className="p-2 bg-white rounded border text-center">
                      <p className="text-xs font-medium">{item.label}</p>
                      <p className="text-[10px] text-gray-500">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Beehiiv Integration */}
          <div className="p-4 border rounded-lg bg-orange-50 border-orange-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-orange-600" />
                  Beehiiv Newsletter Sync
                </h4>
                <p className="text-sm text-gray-500">Auto-sync participants to your Beehiiv newsletter</p>
              </div>
              <Switch
                checked={settings.beehiiv_enabled !== false}
                onCheckedChange={(checked) => updateSetting("beehiiv_enabled", checked)}
              />
            </div>
            
            {settings.beehiiv_enabled !== false && (
              <div className="space-y-3 pt-4 border-t border-orange-200">
                <div>
                  <Label>Beehiiv Tags (comma-separated)</Label>
                  <Input
                    value={settings.beehiiv_tags || ""}
                    onChange={(e) => updateSetting("beehiiv_tags", e.target.value)}
                    placeholder="pilot_mua, beauty_partner, 2026_cohort"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">These tags will be applied to all participants in this pilot</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs bg-white">
                    {settings.beehiiv_tags ? settings.beehiiv_tags.split(',').length : 0} tags configured
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* HOST DASHBOARD SECTION - Customizable Text & Settings */}
      {activeSection === "host_dashboard" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-lg">Host Dashboard Customization</h3>
              <p className="text-sm text-gray-500">Customize all text, labels, and sections that hosts see when managing events</p>
            </div>
            {selectedCategory?.events?.[0] && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const eventId = selectedCategory.events[0].event_id || selectedCategory.events[0];
                  window.open(`/host/v2/${eventId}`, '_blank');
                }}
                className="gap-2"
                data-testid="preview-host-dashboard-btn"
              >
                <Eye className="w-4 h-4" /> Preview as Host
              </Button>
            )}
          </div>

          {/* Overview Section Labels */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4 text-purple-600" />
              Overview Tab Labels
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Quick Stats Title</Label>
                <Input
                  value={settings.host_overview_stats_title || "Quick Stats"}
                  onChange={(e) => updateSetting("host_overview_stats_title", e.target.value)}
                  placeholder="Quick Stats"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Quick Actions Title</Label>
                <Input
                  value={settings.host_overview_actions_title || "Quick Actions"}
                  onChange={(e) => updateSetting("host_overview_actions_title", e.target.value)}
                  placeholder="Quick Actions"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Your Links Section Title</Label>
                <Input
                  value={settings.host_your_links_title || "Your Links"}
                  onChange={(e) => updateSetting("host_your_links_title", e.target.value)}
                  placeholder="Your Links"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Team Section Title</Label>
                <Input
                  value={settings.host_team_title || "Team & People"}
                  onChange={(e) => updateSetting("host_team_title", e.target.value)}
                  placeholder="Team & People"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Stat Card Labels */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Stat Card Labels</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Total Attending Label</Label>
                <Input
                  value={settings.stat_total_attending_label || "Total Attending"}
                  onChange={(e) => updateSetting("stat_total_attending_label", e.target.value)}
                  placeholder="Total Attending"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>RSVPs Label</Label>
                <Input
                  value={settings.stat_rsvps_label || "RSVPs"}
                  onChange={(e) => updateSetting("stat_rsvps_label", e.target.value)}
                  placeholder="RSVPs"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Checked In Label</Label>
                <Input
                  value={settings.stat_checked_in_label || "Checked In"}
                  onChange={(e) => updateSetting("stat_checked_in_label", e.target.value)}
                  placeholder="Checked In"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Quick Action Button Labels */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Quick Action Button Labels</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Share Preferences Button</Label>
                <Input
                  value={settings.btn_share_preferences || "Share Preferences"}
                  onChange={(e) => updateSetting("btn_share_preferences", e.target.value)}
                  placeholder="Share Preferences"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Invite People Button</Label>
                <Input
                  value={settings.btn_invite_people || "Invite People"}
                  onChange={(e) => updateSetting("btn_invite_people", e.target.value)}
                  placeholder="Invite People"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Manage Team Button</Label>
                <Input
                  value={settings.btn_manage_team || "Manage Team"}
                  onChange={(e) => updateSetting("btn_manage_team", e.target.value)}
                  placeholder="Manage Team"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>View Analytics Button</Label>
                <Input
                  value={settings.btn_view_analytics || "View Analytics"}
                  onChange={(e) => updateSetting("btn_view_analytics", e.target.value)}
                  placeholder="View Analytics"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Tab Labels */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Navigation Tab Labels</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Overview Tab</Label>
                <Input
                  value={settings.tab_overview || "Overview"}
                  onChange={(e) => updateSetting("tab_overview", e.target.value)}
                  placeholder="Overview"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>People Tab</Label>
                <Input
                  value={settings.tab_people || "People"}
                  onChange={(e) => updateSetting("tab_people", e.target.value)}
                  placeholder="People"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Analytics Tab</Label>
                <Input
                  value={settings.tab_analytics || "Analytics"}
                  onChange={(e) => updateSetting("tab_analytics", e.target.value)}
                  placeholder="Analytics"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Settings Tab</Label>
                <Input
                  value={settings.tab_settings || "Settings"}
                  onChange={(e) => updateSetting("tab_settings", e.target.value)}
                  placeholder="Settings"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Section Visibility */}
          <div className="p-4 border rounded-lg bg-purple-50 border-purple-200">
            <h4 className="font-medium mb-3">Section Visibility for Hosts</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { key: "show_dietary_section", label: "Dietary Preferences", desc: "Show dietary section" },
                { key: "show_accessibility_section", label: "Accessibility Needs", desc: "Show accessibility section" },
                { key: "show_music_section", label: "Music Preferences", desc: "Show music/Tap-In section" },
                { key: "show_interests_section", label: "Interests & Preferences", desc: "Show interests section" },
                { key: "show_analytics_tab", label: "Analytics Tab", desc: "Show analytics tab to hosts" },
                { key: "show_team_management", label: "Team Management", desc: "Allow team member management" }
              ].map(toggle => (
                <div key={toggle.key} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                  <div>
                    <p className="font-medium text-sm">{toggle.label}</p>
                    <p className="text-xs text-gray-500">{toggle.desc}</p>
                  </div>
                  <Switch
                    checked={settings[toggle.key] !== false}
                    onCheckedChange={(checked) => updateSetting(toggle.key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMATION & GATED CONTENT SECTION */}
      {activeSection === "confirmation" && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium text-lg">Confirmation & Gated Content</h3>
            <p className="text-sm text-gray-500">Configure what guests see after checking in, including gated content for virtual events</p>
          </div>

          {/* Confirmation Page Header */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Confirmation Page Header</h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Confirmation Title</Label>
                <Input
                  value={settings.confirmation_title || "You're Checked In!"}
                  onChange={(e) => updateSetting("confirmation_title", e.target.value)}
                  placeholder="You're Checked In!"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Confirmation Subtitle</Label>
                <Input
                  value={settings.confirmation_subtitle || "Welcome to the event"}
                  onChange={(e) => updateSetting("confirmation_subtitle", e.target.value)}
                  placeholder="Welcome to the event"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Confirmation Message</Label>
                <Textarea
                  value={settings.confirmation_message || "Thank you for checking in. Here's your event access:"}
                  onChange={(e) => updateSetting("confirmation_message", e.target.value)}
                  placeholder="Thank you for checking in. Here's your event access:"
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Gated Content Settings */}
          <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Gated Content
                </h4>
                <p className="text-sm text-gray-500">Content revealed only after check-in (great for Zoom links, resources)</p>
              </div>
              <Switch
                checked={settings.gated_content_enabled || false}
                onCheckedChange={(checked) => updateSetting("gated_content_enabled", checked)}
              />
            </div>
            
            {settings.gated_content_enabled && (
              <div className="space-y-4 pt-4 border-t border-blue-200">
                <div>
                  <Label>Gated Content Title</Label>
                  <Input
                    value={settings.gated_content_title || "Access Your Content"}
                    onChange={(e) => updateSetting("gated_content_title", e.target.value)}
                    placeholder="Access Your Content"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Pre-Checkin Message (shown before check-in)</Label>
                  <Textarea
                    value={settings.gated_pre_checkin_message || "Check in to unlock your event access and materials."}
                    onChange={(e) => updateSetting("gated_pre_checkin_message", e.target.value)}
                    placeholder="Check in to unlock your event access and materials."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Post-Checkin Content (supports markdown)</Label>
                  <Textarea
                    value={settings.gated_content_body || ""}
                    onChange={(e) => updateSetting("gated_content_body", e.target.value)}
                    placeholder="## Welcome!\n\nHere's your Zoom link: [Join Meeting](https://zoom.us/...)\n\n### Resources\n- Workshop slides\n- Follow-along guide"
                    className="mt-1 font-mono text-sm"
                    rows={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">Use markdown for formatting. Add Zoom links, PDFs, slides, etc.</p>
                </div>
              </div>
            )}
          </div>

          {/* Virtual Event Settings */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Video className="w-4 h-4 text-indigo-600" />
              Virtual Event Settings
            </h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Zoom/Meeting Link</Label>
                <Input
                  value={settings.virtual_meeting_link || ""}
                  onChange={(e) => updateSetting("virtual_meeting_link", e.target.value)}
                  placeholder="https://zoom.us/j/..."
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">This link is gated and only shown after check-in</p>
              </div>
              <div>
                <Label>Meeting Password (optional)</Label>
                <Input
                  value={settings.virtual_meeting_password || ""}
                  onChange={(e) => updateSetting("virtual_meeting_password", e.target.value)}
                  placeholder="Meeting password"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Join Meeting Button Text</Label>
                <Input
                  value={settings.btn_join_meeting || "Join Zoom Meeting"}
                  onChange={(e) => updateSetting("btn_join_meeting", e.target.value)}
                  placeholder="Join Zoom Meeting"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Check-in Button Labels */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-medium mb-3">Check-in Flow Text</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Check-in Button Text</Label>
                <Input
                  value={settings.btn_checkin || "Check In"}
                  onChange={(e) => updateSetting("btn_checkin", e.target.value)}
                  placeholder="Check In"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Already Checked In Text</Label>
                <Input
                  value={settings.text_already_checked_in || "You're already checked in!"}
                  onChange={(e) => updateSetting("text_already_checked_in", e.target.value)}
                  placeholder="You're already checked in!"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Check-in Success Message</Label>
                <Input
                  value={settings.text_checkin_success || "Successfully checked in!"}
                  onChange={(e) => updateSetting("text_checkin_success", e.target.value)}
                  placeholder="Successfully checked in!"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>NFC Tap Instruction</Label>
                <Input
                  value={settings.text_nfc_instruction || "Tap your badge to check in"}
                  onChange={(e) => updateSetting("text_nfc_instruction", e.target.value)}
                  placeholder="Tap your badge to check in"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Preview Card */}
          <div className="p-4 border rounded-lg bg-white">
            <h4 className="font-medium mb-3 text-sm text-gray-500">Preview: Confirmation Page</h4>
            <div className="border rounded-lg p-6 bg-gradient-to-br from-green-50 to-emerald-50">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {settings.confirmation_title || "You're Checked In!"}
                </h2>
                <p className="text-gray-600 mb-4">
                  {settings.confirmation_subtitle || "Welcome to the event"}
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  {settings.confirmation_message || "Thank you for checking in. Here's your event access:"}
                </p>
                {settings.gated_content_enabled && (
                  <div className="bg-white rounded-lg p-4 border text-left">
                    <h3 className="font-medium mb-2">{settings.gated_content_title || "Access Your Content"}</h3>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                      {settings.gated_content_body || "Your gated content will appear here..."}
                    </p>
                  </div>
                )}
                {settings.virtual_meeting_link && (
                  <Button className="mt-4 bg-blue-600 hover:bg-blue-700">
                    <Video className="w-4 h-4 mr-2" />
                    {settings.btn_join_meeting || "Join Zoom Meeting"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      {activeSection !== "preview" && (
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Settings
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PORTAL UPDATES TAB - Shows updates assigned to this portal
// ============================================================================
function PortalUpdatesTab({ subCategoryId }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/updates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Filter to updates assigned to this portal or all portals
          const filtered = (data.updates || []).filter(
            u => u.assigned_portals?.length === 0 || u.assigned_portals?.includes(subCategoryId)
          );
          setUpdates(filtered);
        }
      } catch (err) {
        console.error("Failed to fetch updates:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUpdates();
  }, [subCategoryId]);

  const typeConfig = {
    announcement: { icon: Clock, color: "bg-blue-500", label: "Announcement" },
    feature: { icon: Sparkles, color: "bg-purple-500", label: "Feature" },
    bug_fix: { icon: Clock, color: "bg-red-500", label: "Bug Fix" },
    improvement: { icon: Settings, color: "bg-green-500", label: "Improvement" }
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (updates.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="font-medium">No updates for this portal</p>
        <p className="text-sm text-gray-500 mt-1">
          Create updates in Product &gt; Updates and assign them here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {updates.map((update) => {
        const config = typeConfig[update.update_type] || typeConfig.announcement;
        const Icon = config.icon;
        return (
          <div
            key={update.update_id}
            className={`p-4 border rounded-lg ${update.is_pinned ? "border-purple-300 bg-purple-50/50" : ""}`}
          >
            <div className="flex items-center gap-2 text-sm mb-2">
              <div className={`w-6 h-6 rounded ${config.color} text-white flex items-center justify-center`}>
                <Icon className="w-3 h-3" />
              </div>
              <span className="font-medium text-gray-700">{config.label}</span>
              <Badge variant={update.status === "published" ? "default" : "secondary"} className="text-xs">
                {update.status}
              </Badge>
              {update.is_pinned && <Badge variant="outline" className="text-xs">Pinned</Badge>}
            </div>
            <h4 className="font-medium">{update.title}</h4>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{update.content}</p>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// NDA SIGNATURES VIEWER - Shows signed NDAs for portal members with PDF download
// ============================================================================
function NDASignaturesViewer({ members, portalId, subCategoryId }) {
  const [ndaSignatures, setNdaSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNda, setSelectedNda] = useState(null);

  useEffect(() => {
    // Filter members who have signed NDA
    const signedMembers = members.filter(m => m.nda_signed);
    setNdaSignatures(signedMembers);
  }, [members]);

  // Download NDA as PDF
  const downloadNdaPdf = async (member) => {
    try {
      const token = localStorage.getItem("session_token");
      const inviteId = member.invite_id || member.member_id || member.portal_id;
      
      // Fetch signed NDA data
      const res = await fetch(`${API}/admin/nda/signed/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let ndaData = {};
      if (res.ok) {
        ndaData = await res.json();
      }
      
      // Generate PDF
      const doc = new jsPDF();
      
      // Header with logo/branding
      doc.setFillColor(75, 0, 130);
      doc.rect(0, 0, 210, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("PHYGITAL", 105, 16, { align: "center" });
      
      // Reset colors
      doc.setTextColor(0, 0, 0);
      
      // Title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("NON-DISCLOSURE AGREEMENT", 105, 40, { align: "center" });
      
      // Decorative line
      doc.setDrawColor(75, 0, 130);
      doc.setLineWidth(0.5);
      doc.line(40, 45, 170, 45);
      
      // NDA content
      const ndaContent = ndaData.nda_content || `
MUTUAL NON-DISCLOSURE AGREEMENT

This Non-Disclosure Agreement ("Agreement") is entered into as of the date of electronic signature below.

PARTIES:
• Phygital, LLC ("Disclosing Party")
• ${member.name || "Receiving Party"} ("Receiving Party")

1. PURPOSE
The parties wish to explore a potential business relationship and may need to disclose confidential information to each other.

2. CONFIDENTIAL INFORMATION
"Confidential Information" includes all non-public information disclosed by either party, including but not limited to:
• Business plans, strategies, and operations
• Technical data, trade secrets, and know-how
• Financial information and projections
• Customer and supplier information
• Any other information marked as confidential

3. OBLIGATIONS
The Receiving Party agrees to:
• Keep all Confidential Information strictly confidential
• Use Confidential Information only for the Purpose
• Not disclose Confidential Information to third parties without prior written consent
• Take reasonable measures to protect the confidentiality of the information

4. TERM
This Agreement shall remain in effect for two (2) years from the date of signature.

5. GOVERNING LAW
This Agreement shall be governed by the laws of California.
      `.trim();
      
      // Strip HTML and format
      const plainText = ndaContent
        .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n$1\n')
        .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
        .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\n\n+/g, '\n\n')
        .trim();
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(plainText, 170);
      
      let y = 55;
      const pageHeight = doc.internal.pageSize.height;
      
      for (let i = 0; i < lines.length; i++) {
        if (y > pageHeight - 70) {
          doc.addPage();
          y = 20;
        }
        doc.text(lines[i], 20, y);
        y += 5;
      }
      
      // Signature section
      if (y > pageHeight - 80) {
        doc.addPage();
        y = 30;
      }
      
      y += 15;
      doc.setDrawColor(75, 0, 130);
      doc.line(20, y, 190, y);
      y += 12;
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("ELECTRONIC SIGNATURE", 20, y);
      y += 10;
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("This document has been electronically signed and is legally binding.", 20, y);
      y += 15;
      
      // Signature details
      doc.setFontSize(11);
      doc.text("Signed by:", 20, y);
      doc.setFont("times", "italic");
      doc.setFontSize(16);
      doc.text(ndaData.signer_name || member.name || "Unknown", 55, y);
      y += 12;
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Email: ${ndaData.signer_email || member.email || "N/A"}`, 20, y);
      y += 7;
      doc.text(`Date Signed: ${member.nda_signed_at ? new Date(member.nda_signed_at).toLocaleDateString() : new Date().toLocaleDateString()}`, 20, y);
      y += 7;
      doc.text(`Timestamp: ${member.nda_signed_at || new Date().toISOString()}`, 20, y);
      y += 7;
      doc.text(`Document ID: NDA-${inviteId}`, 20, y);
      
      // Footer
      y = pageHeight - 15;
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text("This electronically signed document is valid and enforceable under the E-SIGN Act.", 105, y, { align: "center" });
      doc.text("© 2026 Phygital Systems. All rights reserved.", 105, y + 5, { align: "center" });
      
      // Save
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `Phygital_NDA_${(member.name || "Unknown").replace(/\s+/g, '_')}_${dateStr}.pdf`;
      doc.save(filename);
      
      toast.success("NDA downloaded as PDF!");
    } catch (err) {
      console.error("Failed to download NDA:", err);
      toast.error("Error downloading NDA");
    }
  };

  if (ndaSignatures.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">No NDA signatures yet</p>
        <p className="text-sm text-gray-400 mt-1">Members will appear here once they sign the NDA</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Signed NDAs ({ndaSignatures.length})</h3>
      </div>
      
      <div className="grid gap-3">
        {ndaSignatures.map((member) => (
          <div 
            key={member.member_id || member.user_id}
            className="bg-white border rounded-lg p-4 hover:border-purple-300 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{member.name || member.email}</p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                  <p className="text-xs text-gray-400">
                    Signed: {member.nda_signed_at ? new Date(member.nda_signed_at).toLocaleString() : "Date unknown"}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => downloadNdaPdf(member)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// INTAKE RESPONSES VIEWER - Shows intake data for each member
// ============================================================================
function IntakeResponsesViewer({ members, pendingInvites }) {
  const [selectedMember, setSelectedMember] = useState(null);
  const [intakeData, setIntakeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  // Download signed NDA as PDF
  const downloadSignedNDA = async (member) => {
    try {
      const token = localStorage.getItem("session_token");
      const inviteId = member.invite_id || member.portal_id;
      
      // Fetch signed NDA data
      const res = await fetch(`${API}/admin/nda/signed/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        toast.error("Failed to fetch NDA data");
        return;
      }
      
      const ndaData = await res.json();
      
      // Generate PDF
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("PHYGITAL", 105, 20, { align: "center" });
      
      doc.setFontSize(14);
      doc.text("NON-DISCLOSURE AGREEMENT", 105, 30, { align: "center" });
      
      // Add horizontal line
      doc.setDrawColor(128, 0, 128);
      doc.setLineWidth(0.5);
      doc.line(20, 35, 190, 35);
      
      // Strip HTML tags for plain text
      const plainText = (ndaData.nda_content || "")
        .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n$1\n')
        .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
        .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\n\n+/g, '\n\n')
        .trim();
      
      // Add NDA content
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(plainText, 170);
      
      let y = 45;
      const pageHeight = doc.internal.pageSize.height;
      
      for (let i = 0; i < lines.length; i++) {
        if (y > pageHeight - 60) {
          doc.addPage();
          y = 20;
        }
        doc.text(lines[i], 20, y);
        y += 5;
      }
      
      // Add signature section
      if (y > pageHeight - 80) {
        doc.addPage();
        y = 30;
      }
      
      y += 15;
      doc.setDrawColor(128, 0, 128);
      doc.line(20, y, 190, y);
      y += 10;
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("ELECTRONIC SIGNATURE", 20, y);
      y += 10;
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("This document has been electronically signed and is legally binding.", 20, y);
      y += 15;
      
      // Signature
      doc.setFontSize(11);
      doc.text("Signed by:", 20, y);
      doc.setFont("times", "italic");
      doc.setFontSize(14);
      doc.text(ndaData.signer_name || "Unknown", 50, y);
      y += 10;
      
      // Date and timestamp
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Email: ${ndaData.signer_email || "N/A"}`, 20, y);
      y += 6;
      doc.text(`Date: ${ndaData.signed_at ? new Date(ndaData.signed_at).toLocaleDateString() : "N/A"}`, 20, y);
      y += 6;
      doc.text(`Timestamp: ${ndaData.signed_at || "N/A"}`, 20, y);
      y += 6;
      doc.text(`Document ID: NDA-${inviteId}`, 20, y);
      
      // Footer
      y += 15;
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text("This electronically signed document is valid and enforceable under the E-SIGN Act.", 105, y, { align: "center" });
      doc.text("© 2026 Phygital Systems. All rights reserved.", 105, y + 5, { align: "center" });
      
      // Save
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `Phygital_NDA_Signed_${(ndaData.signer_name || "Unknown").replace(/\s+/g, '_')}_${dateStr}.pdf`;
      doc.save(filename);
      
      toast.success("NDA downloaded!");
    } catch (err) {
      console.error("Failed to download NDA:", err);
      toast.error("Error downloading NDA");
    }
  };

  const fetchIntakeData = async (member) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      // Build query params with all possible identifiers for lookup
      const params = new URLSearchParams();
      if (member.member_id) params.append("member_id", member.member_id);
      if (member.invite_id) params.append("invite_id", member.invite_id);
      if (member.user_id) params.append("user_id", member.user_id);
      
      // Use portal_id if available, otherwise use member_id as fallback
      const lookupId = member.portal_id || member.member_id || "unknown";
      const queryString = params.toString() ? `?${params.toString()}` : "";
      
      const res = await fetch(`${API}/admin/portal/${lookupId}/intake${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log("Intake data fetched:", data);
        setIntakeData(data.intake_responses || {});
      } else {
        console.error("Failed to fetch intake:", res.status);
        setIntakeData({});
      }
    } catch (err) {
      console.error("Failed to fetch intake:", err);
      setIntakeData({});
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setAdminNotes(member.admin_notes || "");
    // Always try to fetch intake data using all available identifiers
    fetchIntakeData(member);
  };
  
  // Save admin notes
  const handleSaveAdminNotes = async () => {
    if (!selectedMember) return;
    setSavingNotes(true);
    try {
      const token = localStorage.getItem("session_token");
      const memberId = selectedMember.member_id || selectedMember.invite_id;
      const res = await fetch(`${API}/admin/portal-members/${memberId}/notes`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ admin_notes: adminNotes })
      });
      if (res.ok) {
        toast.success("Admin notes saved!");
        // Update local state
        setSelectedMember(prev => ({ ...prev, admin_notes: adminNotes }));
      } else {
        toast.error("Failed to save notes");
      }
    } catch (err) {
      toast.error("Error saving notes");
    } finally {
      setSavingNotes(false);
    }
  };

  // Field groupings for display - expanded to cover ALL intake fields
  const fieldGroups = {
    "Profile Info": ["first_name", "last_name", "business_name", "email", "phone", "location", "website"],
    "Discovery": ["primary_discovery", "primary_discovery_channels", "discovery_channels", "discovery_other", "instagram", "tiktok", "facebook", "google_business", "yelp"],
    "Booking & Scheduling": ["booking_method", "booking_platform", "booking_platforms", "booking_method_other", "booking_platform_other", "typical_response_time", "styleseat", "booksy"],
    "Service Info": ["service_location", "services_offered", "years_experience", "pricing_range", "appointment_prep", "appointment_prep_other", "what_to_bring"],
    "Client Management": ["follow_up_method", "rebooking_approach", "client_retention_notes", "client_defaults", "client_defaults_other"],
    "Payments & Pricing": ["payment_methods", "payment_other", "show_prices", "require_deposit", "pricing_discussion", "offers_packages", "packages_details"],
    "Preferences & Setup": ["send_reminders", "collect_preferences", "tech_tools", "inspo_collection_method", "inspo_platforms", "uses_mood_boards"],
    "Business Details": ["common_questions", "info_wish_clients_knew", "brands_used", "sell_products", "products_sold", "product_recommendations"],
    "Provider Workflow": ["provider_prep_routine", "supplies_setup", "review_client_history", "cancellation_policy"],
    "Branding": ["has_logo", "no_logo", "logo_url"],
    "Additional": ["anything_else", "schedule_call", "admin_notes"]
  };
  
  // Get all fields that don't fit into predefined groups
  const getAllFields = () => {
    if (!intakeData) return [];
    const definedFields = new Set(Object.values(fieldGroups).flat());
    return Object.keys(intakeData).filter(key => !definedFields.has(key) && key !== "_id");
  };

  const formatValue = (value) => {
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object" && value !== null) {
      return Object.entries(value)
        .filter(([k, v]) => v)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value || " - ";
  };

  return (
    <div className="space-y-4">
      {/* Member Selection */}
      <div className="flex flex-wrap gap-2">
        {members.map((member) => (
          <button
            key={member.user_id || member.portal_id}
            onClick={() => handleSelectMember(member)}
            className={`px-4 py-2 rounded-lg text-sm border transition-colors flex items-center gap-2 ${
              selectedMember?.user_id === member.user_id
                ? "bg-purple-100 border-purple-300 text-purple-700"
                : "bg-white border-gray-200 hover:border-purple-300"
            }`}
          >
            <User className="w-4 h-4" />
            {member.name || member.email || "Unknown"}
            {member.intake_completed && (
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            )}
          </button>
        ))}
      </div>

      {/* Intake Data Display */}
      {selectedMember && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              {selectedMember.name || selectedMember.email}
              {selectedMember.intake_completed ? (
                <Badge className="bg-green-500 text-white">Intake Complete</Badge>
              ) : (
                <Badge variant="secondary">Pending</Badge>
              )}
            </CardTitle>
            {selectedMember.business_name && (
              <CardDescription>{selectedMember.business_name}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {/* NDA Status and Download */}
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium">Non-Disclosure Agreement</p>
                    {selectedMember.nda_signed ? (
                      <p className="text-sm text-green-600">
                        Signed on {new Date(selectedMember.nda_signed_at).toLocaleDateString()}
                      </p>
                    ) : (
                      <p className="text-sm text-amber-600">Not yet signed</p>
                    )}
                  </div>
                </div>
                {selectedMember.nda_signed && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => downloadSignedNDA(selectedMember)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                )}
              </div>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                <p className="text-sm text-gray-500 mt-2">Loading intake data...</p>
              </div>
            ) : intakeData && Object.keys(intakeData).length > 0 ? (
              <div className="space-y-6">
                {/* Admin Internal Notes Section - ALWAYS SHOW */}
                <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-amber-800 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Internal Admin Notes
                      <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">Admin Only</Badge>
                    </h4>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleSaveAdminNotes}
                      disabled={savingNotes}
                      className="text-amber-700 border-amber-300 hover:bg-amber-100"
                    >
                      {savingNotes ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                      Save Notes
                    </Button>
                  </div>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add private notes about this participant (only visible to admins)..."
                    className="bg-white border-amber-200 focus:border-amber-400"
                    rows={3}
                  />
                </div>
                
                {/* Predefined field groups */}
                {Object.entries(fieldGroups).map(([groupName, fields]) => {
                  const groupFields = fields.filter(f => intakeData[f] !== undefined && intakeData[f] !== "" && intakeData[f] !== null);
                  if (groupFields.length === 0) return null;
                  
                  return (
                    <div key={groupName}>
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-500" />
                        {groupName}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {groupFields.map((field) => (
                          <div key={field} className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1 capitalize">
                              {field.replace(/_/g, " ")}
                            </p>
                            <p className="text-sm font-medium">
                              {formatValue(intakeData[field])}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                
                {/* Other fields not in predefined groups */}
                {getAllFields().length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      Other Responses
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getAllFields().map((field) => (
                        <div key={field} className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                          <p className="text-xs text-orange-600 mb-1 capitalize">
                            {field.replace(/_/g, " ")}
                          </p>
                          <p className="text-sm font-medium">
                            {formatValue(intakeData[field])}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Show total count of intake fields */}
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-gray-400">
                    Total fields: {Object.keys(intakeData).filter(k => k !== "_id").length} | 
                    Source: {selectedMember?.member_id || selectedMember?.portal_id || "unknown"}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Admin Internal Notes Section - ALWAYS SHOW even when no intake data */}
                <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-amber-800 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Internal Admin Notes
                      <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">Admin Only</Badge>
                    </h4>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleSaveAdminNotes}
                      disabled={savingNotes}
                      className="text-amber-700 border-amber-300 hover:bg-amber-100"
                    >
                      {savingNotes ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                      Save Notes
                    </Button>
                  </div>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add private notes about this participant (only visible to admins)..."
                    className="bg-white border-amber-200 focus:border-amber-400"
                    rows={3}
                  />
                </div>
                
                <div className="text-center py-8 border rounded-lg">
                  <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium text-gray-600">No intake data yet</p>
                  <p className="text-sm text-gray-500 mt-1">
                    This member hasn't completed their intake questionnaire
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedMember && members.length > 0 && (
        <div className="text-center py-8 border rounded-lg">
          <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="font-medium text-gray-600">Select a member</p>
          <p className="text-sm text-gray-500 mt-1">
            Choose a member above to view their intake responses
          </p>
        </div>
      )}

      {members.length === 0 && (
        <div className="text-center py-8 border rounded-lg">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="font-medium text-gray-600">No members yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Create invites and once members join, their intake responses will appear here
          </p>
        </div>
      )}
    </div>
  );
}
